/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.4 — CANONICAL YOGAS & DOSHAS CORE ENGINE
 * ============================================================================
 * Single Authoritative Calculation Core for:
 * 1. Nabhasa Yogas (32 Classical: Ashraya, Dala, Akriti, Sankhya)
 * 2. Pancha Mahapurusha Yogas (Ruchaka, Bhadra, Hamsa, Malavya, Sasa)
 * 3. Classical Solar & Lunar Yogas (Sunapha, Anapha, Durdhara, Kemadruma, Veshi, Voshi, Ubhayachari)
 * 4. Classical Sovereign & Auspicious Yogas (Gajakesari, Budhaditya, Lakshmi, Saraswati, Amala, Parvata, Kahala)
 * 5. Dhana Yogas (Wealth Circuits: 1st, 2nd, 5th, 9th, 11th lord interactions)
 * 6. Raja Yogas (Kendra-Trikona lordship, Dharma-Karmadhipati, Viparita Harsha/Sarala/Vimala, Neecha Bhanga)
 * 7. Canonical Manglik Dosha (Lagna, Moon, Venus with 12 classical cancellations)
 * 8. Canonical Kaal Sarp & Kalamrita Yoga (Rahu-Ketu axis, boundary orb, 12 types, ADR-4 compliant)
 *
 * Classical Authority:
 * - Brihat Parashara Hora Shastra (BPHS Ch. 34–42, 75)
 * - Phaladeepika (Ch. 6)
 * - Saravali (Ch. 21, 35)
 * - Muhurta Chintamani
 *
 * Layer Integration:
 * - Layer 6 (Ashtakavarga SAV bindus >= 28 qualify fruition)
 * - Layer 7 (Shadbala ratio >= 1.0 qualifies operational strength)
 * - Epistemic taxonomy: Calculated Fact vs Classical Rule vs Fruition Assessment
 * ============================================================================
 */

import { 
  CompleteKundali, 
  PlanetName, 
  PlanetPosition, 
  ZodiacSign,
  HouseInfo,
  DetectedYoga, 
  YogaCategory, 
  YogaFruitionStatus,
  YogaMethodologyCategory,
  ManglikAnalysis, 
  KaalSarpAnalysis 
} from '../types/kundali';

export const ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export const ZODIAC_LORDS: { [key in ZodiacSign]: PlanetName } = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

function resolveSavForBhava(kundali: any, bhava: number): number {
  const av = kundali?.ashtakvarga || kundali?.ashtakavarga;
  if (!av) return 28;
  if (av.byHouse?.sarvashtakvarga && Array.isArray(av.byHouse.sarvashtakvarga)) {
    return av.byHouse.sarvashtakvarga[bhava - 1] ?? 28;
  }
  if (Array.isArray(av.sarvashtakvarga)) {
    if (kundali.houses && kundali.houses[bhava - 1]) {
      const signIdx = kundali.houses[bhava - 1].signIndex;
      return av.sarvashtakvarga[signIdx] ?? 28;
    }
    return av.sarvashtakvarga[bhava - 1] ?? 28;
  }
  return 28;
}

export interface YogaEvaluationResult {
  yogas: DetectedYoga[];
  manglik: ManglikAnalysis;
  kaalSarp: KaalSarpAnalysis;
  summary: {
    totalDetected: number;
    highPotencyCount: number;
    moderatePotencyCount: number;
    dormantOrAfflictedCount: number;
    hasMajorRajaYoga: boolean;
    hasMajorDhanaYoga: boolean;
    hasMahapurushaYoga: boolean;
  };
}

const SEVEN_CLASSICAL_PLANETS: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
const NATURAL_BENEFICS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
const NATURAL_MALEFICS: PlanetName[] = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];

// Sign Classifications (0 = Aries ... 11 = Pisces)
const MOVABLE_SIGNS = [0, 3, 6, 9];      // Chara (Aries, Cancer, Libra, Capricorn)
const FIXED_SIGNS = [1, 4, 7, 10];       // Sthira (Taurus, Leo, Scorpio, Aquarius)
const DUAL_SIGNS = [2, 5, 8, 11];        // Dvisvabhava (Gemini, Virgo, Sagittarius, Pisces)

// Exaltation & Debilitation Signs
const EXALTATION_SIGNS: Record<PlanetName, number> = {
  Sun: 0,      // Aries
  Moon: 1,     // Taurus
  Mars: 9,     // Capricorn
  Mercury: 5,  // Virgo
  Jupiter: 3,  // Cancer
  Venus: 11,   // Pisces
  Saturn: 6,   // Libra
  Rahu: 1,     // Taurus (classical consensus)
  Ketu: 7,     // Scorpio
  Ascendant: 0
};

const DEBILITATION_SIGNS: Record<PlanetName, number> = {
  Sun: 6,      // Libra
  Moon: 7,     // Scorpio
  Mars: 3,     // Cancer
  Mercury: 11, // Pisces
  Jupiter: 9,  // Capricorn
  Venus: 5,    // Virgo
  Saturn: 0,   // Aries
  Rahu: 7,     // Scorpio
  Ketu: 1,     // Taurus
  Ascendant: 6
};

const OWN_SIGNS: Record<PlanetName, number[]> = {
  Sun: [4],          // Leo
  Moon: [3],         // Cancer
  Mars: [0, 7],      // Aries, Scorpio
  Mercury: [2, 5],   // Gemini, Virgo
  Jupiter: [8, 11],  // Sagittarius, Pisces
  Venus: [1, 6],     // Taurus, Libra
  Saturn: [9, 10],   // Capricorn, Aquarius
  Rahu: [10],        // Aquarius co-lord
  Ketu: [7],         // Scorpio co-lord
  Ascendant: []
};

// ----------------------------------------------------------------------------
// HELPER ROUTINES
// ----------------------------------------------------------------------------

function getHouseFromLagna(planetSignIndex: number, ascSignIndex: number): number {
  return ((planetSignIndex - ascSignIndex + 12) % 12) + 1;
}

function getHouseDifference(h1: number, h2: number): number {
  return ((h1 - h2 + 12) % 12) + 1;
}

function getLordOfSign(signIndex: number): PlanetName {
  const sign = ZODIAC_SIGNS[signIndex % 12];
  return ZODIAC_LORDS[sign] as PlanetName;
}

function getBhaveshLord(houseNumber: number, ascSignIndex: number): PlanetName {
  const signIndex = (ascSignIndex + houseNumber - 1) % 12;
  return getLordOfSign(signIndex);
}

function areMutuallyAspecting(p1: PlanetPosition, p2: PlanetPosition): boolean {
  // Classical 7th aspect holds for all planets
  const dist1to2 = ((p2.house - p1.house + 12) % 12) + 1;
  const dist2to1 = ((p1.house - p2.house + 12) % 12) + 1;
  return dist1to2 === 7 && dist2to1 === 7;
}

/**
 * Computes fruition and operational potency metrics (PRODUCT_SYNTHESIS_FRUITION).
 * NOTE ON CLASSICAL METHODOLOGY:
 * Yoga formation is established strictly by classical natal planetary geometry (BPHS Ch. 35).
 * Layer 6 Ashtakavarga (SAV bindus >= 28) and Layer 7 Shadbala (ratio >= 1.0) are corroborating
 * operational strength factors that modulate fruition potency (HIGH_POTENCY, MODERATE_POTENCY,
 * AFFLICTED_OR_DORMANT), and do NOT alter the classical geometric formation criteria.
 */
function computeFruitionMetrics(
  planets: PlanetPosition[],
  houses: number[],
  kundali: CompleteKundali,
  baseScore: number
): { strengthScore: number; fruitionStatus: YogaFruitionStatus; qualifying: string[]; afflictions: string[] } {
  const qualifying: string[] = [];
  const afflictions: string[] = [];
  let scoreMod = baseScore;

  // Layer 7 Shadbala evaluation
  const shadbala = kundali.shadbala;
  let shadbalaSum = 0;
  let shadbalaCount = 0;

  for (const p of planets) {
    if (shadbala && shadbala.planets) {
      const sp = shadbala.planets.find(s => s.planet === p.name);
      if (sp) {
        const ratio = sp.ratio ?? (sp as any).shadbalaRatio ?? 1.0;
        shadbalaSum += ratio;
        shadbalaCount++;
        if (ratio >= 1.0) {
          qualifying.push(`${p.name} possesses adequate Shadbala (${ratio.toFixed(2)} ratio)`);
          scoreMod += 4;
        } else {
          afflictions.push(`${p.name} suffers Shadbala deficit (${ratio.toFixed(2)} ratio < 1.0)`);
          scoreMod -= 6;
        }
      }
    }

    if (p.isCombust) {
      afflictions.push(`${p.name} is combust by Sun`);
      scoreMod -= 8;
    }

    if (p.dignity === 'Exalted' || p.dignity === 'Own House' || p.dignity === 'Moolatrikona') {
      qualifying.push(`${p.name} is endowed with high dignity (${p.dignity})`);
      scoreMod += 5;
    } else if (p.dignity === 'Debilitated') {
      afflictions.push(`${p.name} is debilitated in D1`);
      scoreMod -= 10;
    }
  }

  // Layer 6 Ashtakavarga SAV evaluation
  for (const h of houses) {
    const savBindus = resolveSavForBhava(kundali, h);
    if (savBindus >= 28) {
      qualifying.push(`House ${h} supported by strong SAV (${savBindus} bindus >= 28)`);
      scoreMod += 3;
    } else if (savBindus < 25) {
      afflictions.push(`House ${h} constrained by weak SAV (${savBindus} bindus < 25)`);
      scoreMod -= 5;
    }
  }

  const finalScore = Math.max(10, Math.min(99, Math.round(scoreMod)));
  let fruitionStatus: YogaFruitionStatus = 'MODERATE_POTENCY';

  if (finalScore >= 70 && afflictions.length === 0) {
    fruitionStatus = 'HIGH_POTENCY';
  } else if (finalScore < 45 || afflictions.length >= 2) {
    fruitionStatus = 'AFFLICTED_OR_DORMANT';
  }

  return { strengthScore: finalScore, fruitionStatus, qualifying, afflictions };
}

// ----------------------------------------------------------------------------
// 1. NABHASA YOGAS EVALUATOR (32 Classical Formations)
// ----------------------------------------------------------------------------

export function evaluateNabhasaYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const sevenGrahas = kundali.planets.filter(p => SEVEN_CLASSICAL_PLANETS.includes(p.name as PlanetName));
  if (sevenGrahas.length < 7) return yogas;

  const ascSign = kundali.planets.find(p => p.name === 'Ascendant')?.signIndex ?? 0;
  const signsOccupied = Array.from(new Set(sevenGrahas.map(p => p.signIndex)));
  const housesOccupied = Array.from(new Set(sevenGrahas.map(p => p.house))).sort((a, b) => a - b);

  let specialAkritiOrAshrayaDetected = false;

  // A. Ashraya Yogas (BPHS Ch. 35, Shlokas 5-6)
  // ARCHITECTURAL DECISION & METHODOLOGY RESOLUTION (F-L8-YD-RA-08):
  // Classical shloka BPHS 35.5 states "चरे रज्जुः स्थिरे मुसलो द्विदेहे नलसंज्ञकः।"
  // However, BPHS 35.21 also explicitly defines Gola Yoga as all planets in a single sign ("एकस्मिन् गोल उच्यते।").
  // If a single sign occupied were allowed to form Ashraya, every sign in the zodiac is
  // Movable, Fixed, or Dual, which would cause Ashraya to suppress Gola 100% of the time,
  // making Gola Yoga extinct in all possible natal charts.
  // Following classical commentator consensus (Dr. B.V. Raman, Gopesh Kumar Ojha), Ashraya yogas
  // ("shelter/refuge") require distribution across at least 2 distinct signs of that quadrate.
  // Single-sign concentrations resolve authoritatively to Gola Sankhya Yoga.
  const allMovable = signsOccupied.length >= 2 && sevenGrahas.every(p => MOVABLE_SIGNS.includes(p.signIndex));
  const allFixed = signsOccupied.length >= 2 && sevenGrahas.every(p => FIXED_SIGNS.includes(p.signIndex));
  const allDual = signsOccupied.length >= 2 && sevenGrahas.every(p => DUAL_SIGNS.includes(p.signIndex));

  if (allMovable) {
    specialAkritiOrAshrayaDetected = true;
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 65);
    yogas.push({
      id: 'nabhasa_ashraya_rajjoo',
      name: 'Rajjoo Yoga (Ashraya)',
      sanskritName: 'रज्जु योग (आश्रय)',
      category: 'NABHASA',
      methodologyCategory: 'SCHOLARLY_INTERPRETATIVE',
      formationRule: 'All seven classical planets exclusively occupy Movable (Chara) signs.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 5',
      shlokaReference: 'चरे रज्जुः स्थिरे मुसलो द्विदेहे नलसंज्ञकः।',
      description: {
        en: 'Rajjoo yoga is formed when all planets reside in cardinal movable signs, conferring restless ambition, continuous journeys, adventurous disposition, and foreign enterprise.',
        hi: 'समस्त सात ग्रह चर राशियों में स्थित होने पर रज्जु योग का निर्माण होता है। यह जातक को भ्रमणशील, साहसी, विदेशगामी एवं निरंतर क्रियाशील बनाता है।'
      },
      result: {
        en: 'Continuous mobility, leadership in pioneering ventures, and success through relocation or dynamic travel.',
        hi: 'निरंतर गतिशीलता, नवीन उद्यमों में सफलता तथा स्थान परिवर्तन द्वारा भाग्योदय।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (allFixed) {
    specialAkritiOrAshrayaDetected = true;
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 70);
    yogas.push({
      id: 'nabhasa_ashraya_musala',
      name: 'Musala Yoga (Ashraya)',
      sanskritName: 'मुसल योग (आश्रय)',
      category: 'NABHASA',
      methodologyCategory: 'SCHOLARLY_INTERPRETATIVE',
      formationRule: 'All seven classical planets exclusively occupy Fixed (Sthira) signs.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 5',
      shlokaReference: 'स्थिरे मुसलसंज्ञकः। दृढचित्तो धनसम्पन्नो विख्यातो भूमिनायकः॥',
      description: {
        en: 'Musala yoga is formed when all planets reside in fixed signs, conferring steadfast determination, unshakable endurance, fixed asset wealth, and enduring reputation.',
        hi: 'समस्त सात ग्रह स्थिर राशियों में स्थित होने पर मुसल योग बनता है। यह जातक को दृढ संकल्पी, अचल संपत्ति से युक्त तथा धीर-गंभीर बनाता है।'
      },
      result: {
        en: 'Enduring stability, institutional permanence, wealth in lands, and steady public honor.',
        hi: 'स्थायी प्रतिष्ठा, अचल संपत्ति एवं जीवन में दीर्घकालिक स्थिरता।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (allDual) {
    specialAkritiOrAshrayaDetected = true;
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 65);
    yogas.push({
      id: 'nabhasa_ashraya_nala',
      name: 'Nala Yoga (Ashraya)',
      sanskritName: 'नल योग (आश्रय)',
      category: 'NABHASA',
      methodologyCategory: 'SCHOLARLY_INTERPRETATIVE',
      formationRule: 'All seven classical planets exclusively occupy Dual (Dvisvabhava) signs.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 5',
      shlokaReference: 'द्विदेहे नलसंज्ञकः। विविधज्ञानकुशलो वाग्मी शिल्पविशारदः॥',
      description: {
        en: 'Nala yoga is formed when all planets reside in dual signs, endowing the native with versatile intellect, dexterity, communicative eloquence, and multi-disciplinary mastery.',
        hi: 'समस्त सात ग्रह द्विस्वभाव राशियों में स्थित होने पर नल योग बनता है। यह बहुमुखी प्रतिभा, शिल्प-कौशल तथा वाक्-चातुर्य प्रदान करता है।'
      },
      result: {
        en: 'Polymath abilities, academic brilliance, and commercial adaptability across dual ventures.',
        hi: 'बहुमुखी ज्ञान, बौद्धिक प्रवीणता तथा व्यापारिक चपलता।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // B. Dala Yogas (Maala / Srik & Sarpa)
  const kendraGrahas = sevenGrahas.filter(p => [1, 4, 7, 10].includes(p.house));
  const nonKendraGrahas = sevenGrahas.filter(p => ![1, 4, 7, 10].includes(p.house));

  const kendraBenefics = kendraGrahas.filter(p => NATURAL_BENEFICS.includes(p.name as PlanetName));
  const kendraMalefics = kendraGrahas.filter(p => NATURAL_MALEFICS.includes(p.name as PlanetName));

  if (kendraBenefics.length > 0 && kendraMalefics.length === 0 && kendraGrahas.length >= 3) {
    specialAkritiOrAshrayaDetected = true;
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(kendraBenefics, [1, 4, 7, 10], kundali, 75);
    yogas.push({
      id: 'nabhasa_dala_maala',
      name: 'Maala (Srik) Yoga (Dala)',
      sanskritName: 'माला (स्रक्) योग (दल)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Benefic planets exclusively occupy Kendras while no malefics are placed in angular houses.',
      participatingPlanets: kendraBenefics.map(p => p.name as PlanetName),
      participatingHouses: [1, 4, 7, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 7',
      shlokaReference: 'केन्द्रेषु शुभसंयुक्ता माला योगः प्रकीर्तितः। नित्यं सुखान्वितो विद्वान् भोक्ता च सुखसम्पदाम्॥',
      description: {
        en: 'Maala yoga occurs when Kendras are graced purely by benefic planets without malefic affliction, conferring continuous prosperity, loving relationships, and refined comfort.',
        hi: 'केन्द्र भावों में केवल शुभ ग्रह स्थित होने पर माला योग बनता है। यह जातक को अखंड सुख, उत्तम वाहन, ऐश्वर्य तथा सज्जनता प्रदान करता है।'
      },
      result: {
        en: 'Unbroken comfort, social grace, abundant conveyances, and honorable livelihood.',
        hi: 'अखंड सुख-समृद्धि, सामाजिक सम्मान तथा ऐश्वर्यशाली जीवन।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (kendraMalefics.length > 0 && kendraBenefics.length === 0 && kendraGrahas.length >= 3) {
    specialAkritiOrAshrayaDetected = true;
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(kendraMalefics, [1, 4, 7, 10], kundali, 45);
    yogas.push({
      id: 'nabhasa_dala_sarpa',
      name: 'Sarpa Yoga (Dala)',
      sanskritName: 'सर्प योग (दल)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Malefic planets exclusively occupy Kendras while no benefics grace angular houses.',
      participatingPlanets: kendraMalefics.map(p => p.name as PlanetName),
      participatingHouses: [1, 4, 7, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 8',
      shlokaReference: 'केन्द्रेषु पापैः संदृष्टः सर्पयोगः प्रकीर्तितः।',
      description: {
        en: 'Sarpa yoga occurs when natural malefics dominate angular houses without benefic relief, indicating arduous early struggles, intense psychological resilience, and hard-won triumph.',
        hi: 'केन्द्रों में केवल पाप ग्रह होने से सर्प योग बनता है। यह प्रारंभिक जीवन में कठोर संघर्ष, परंतु अदम्य सहनशक्ति एवं उत्तरार्ध में विजय का सूचक है।'
      },
      result: {
        en: 'Tough lessons, resilience through adversity, and eventual mastery through perseverance.',
        hi: 'कठिन परिश्रम द्वारा विजय, संघर्षपूर्ण मार्ग तथा परिपक्व सहनशीलता।'
      },
      isAuspicious: false,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // C. 20 Classical Akriti Yogas (BPHS Ch. 35, Shlokas 9–17)
  // Helper to generate circular house arc
  const makeArc = (start: number, length: number): number[] =>
    Array.from({ length }, (_, i) => ((start - 1 + i) % 12) + 1);

  // 1. Gada Yoga: All planets in 2 adjacent Kendras (1&4, 4&7, 7&10, or 10&1)
  const isGada = housesOccupied.length === 2 && (
    (housesOccupied[0] === 1 && housesOccupied[1] === 4) ||
    (housesOccupied[0] === 4 && housesOccupied[1] === 7) ||
    (housesOccupied[0] === 7 && housesOccupied[1] === 10) ||
    (housesOccupied[0] === 1 && housesOccupied[1] === 10)
  );

  // 2. Shakata Yoga: All planets strictly in 1st & 7th Kendras
  const isShakata = housesOccupied.length === 2 && housesOccupied[0] === 1 && housesOccupied[1] === 7;

  // 3. Vihaga (Pakshi) Yoga: All planets strictly in 4th & 10th Kendras
  const isVihaga = housesOccupied.length === 2 && housesOccupied[0] === 4 && housesOccupied[1] === 10;

  // 4. Shringataka Yoga: All planets in Dharma Trikonas (1, 5, 9) — All 3 trines occupied
  const isShringataka = housesOccupied.length === 3 && [1, 5, 9].every(h => housesOccupied.includes(h));

  // 5. Hala Yoga: All planets in any of the other three trikonas (2,6,10 or 3,7,11 or 4,8,12) — All 3 houses occupied
  const isHala = housesOccupied.length === 3 && (
    [2, 6, 10].every(h => housesOccupied.includes(h)) ||
    [3, 7, 11].every(h => housesOccupied.includes(h)) ||
    [4, 8, 12].every(h => housesOccupied.includes(h))
  );

  // 6. Vajra Yoga: Benefics in 1 & 7, Malefics in 4 & 10 (All 4 Kendras occupied)
  const isVajra = housesOccupied.length === 4 && [1, 4, 7, 10].every(h => housesOccupied.includes(h)) &&
    kendraBenefics.length > 0 && kendraBenefics.every(p => [1, 7].includes(p.house)) &&
    kendraMalefics.length > 0 && kendraMalefics.every(p => [4, 10].includes(p.house));

  // 7. Yava Yoga: Malefics in 1 & 7, Benefics in 4 & 10 (All 4 Kendras occupied)
  const isYava = housesOccupied.length === 4 && [1, 4, 7, 10].every(h => housesOccupied.includes(h)) &&
    kendraMalefics.length > 0 && kendraMalefics.every(p => [1, 7].includes(p.house)) &&
    kendraBenefics.length > 0 && kendraBenefics.every(p => [4, 10].includes(p.house));

  // 8. Kamala Yoga: All 4 Kendras occupied by planets
  const isKamala = housesOccupied.length === 4 && [1, 4, 7, 10].every(h => housesOccupied.includes(h)) && !isVajra && !isYava;

  // 9. Vapi Yoga: All 4 Panapharas (2,5,8,11) or all 4 Apoklimas (3,6,9,12) occupied
  const isVapi = housesOccupied.length === 4 && (
    [2, 5, 8, 11].every(h => housesOccupied.includes(h)) ||
    [3, 6, 9, 12].every(h => housesOccupied.includes(h))
  );

  // 10. Yupam Yoga: 4 continuous houses from 1st (1, 2, 3, 4) — All 4 houses occupied
  const isYupam = housesOccupied.length === 4 && [1, 2, 3, 4].every(h => housesOccupied.includes(h));

  // 11. Shara (Bāna) Yoga: 4 continuous houses from 4th (4, 5, 6, 7) — All 4 houses occupied
  const isShara = housesOccupied.length === 4 && [4, 5, 6, 7].every(h => housesOccupied.includes(h));

  // 12. Shakti Yoga: 4 continuous houses from 7th (7, 8, 9, 10) — All 4 houses occupied
  const isShakti = housesOccupied.length === 4 && [7, 8, 9, 10].every(h => housesOccupied.includes(h));

  // 13. Danda Yoga: 4 continuous houses from 10th (10, 11, 12, 1) — All 4 houses occupied
  const isDanda = housesOccupied.length === 4 && [10, 11, 12, 1].every(h => housesOccupied.includes(h));

  // 14. Nauka Yoga: 7 continuous houses from 1st (1..7) — All 7 houses occupied (1 Graha each)
  const isNauka = housesOccupied.length === 7 && [1, 2, 3, 4, 5, 6, 7].every(h => housesOccupied.includes(h));

  // 15. Kutira Yoga: 7 continuous houses from 4th (4..10) — All 7 houses occupied (1 Graha each)
  const isKutira = housesOccupied.length === 7 && [4, 5, 6, 7, 8, 9, 10].every(h => housesOccupied.includes(h));

  // 16. Chhatra Yoga: 7 continuous houses from 7th (7..1) — All 7 houses occupied (1 Graha each)
  const isChhatra = housesOccupied.length === 7 && [7, 8, 9, 10, 11, 12, 1].every(h => housesOccupied.includes(h));

  // 17. Chapa (Dhanus) Yoga: 7 continuous houses from 10th (10..4) — All 7 houses occupied (1 Graha each)
  const isChapa = housesOccupied.length === 7 && [10, 11, 12, 1, 2, 3, 4].every(h => housesOccupied.includes(h));

  // 18. Ardha-Chandra Yoga: 7 continuous houses starting from a Panaphara or Apoklima — All 7 houses occupied
  const nonKendraStarts = [2, 3, 5, 6, 8, 9, 11, 12];
  const isArdhaChandra = housesOccupied.length === 7 && nonKendraStarts.some(s => {
    const arc = makeArc(s, 7);
    return arc.every(h => housesOccupied.includes(h));
  });

  // 19. Chakra Yoga: Alternate odd houses (1, 3, 5, 7, 9, 11) — All 6 odd houses occupied
  const isChakra = housesOccupied.length === 6 && [1, 3, 5, 7, 9, 11].every(h => housesOccupied.includes(h));

  // 20. Samudra Yoga: Alternate even houses (2, 4, 6, 8, 10, 12) — All 6 even houses occupied
  const isSamudra = housesOccupied.length === 6 && [2, 4, 6, 8, 10, 12].every(h => housesOccupied.includes(h));

  // Push Akriti Formations
  if (isGada) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 68);
    yogas.push({
      id: 'nabhasa_akriti_gada',
      name: 'Gada Yoga (Akriti)',
      sanskritName: 'गदा योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets reside exclusively in two adjacent Kendras (1&4, 4&7, 7&10, or 10&1).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 9',
      shlokaReference: 'लग्ने बन्धौ स्थिताः सर्वे खेचरा गदसंज्ञकः। बन्ध्वस्ते चास्तखे वाऽपि खे लग्ने वा गदा भवेत्॥',
      description: {
        en: 'Gada (Mace) yoga forms when planets concentrate in two adjacent angular quadrants, conferring martial fortitude, persistent wealth, sacrificial virtue, and prowess in debate.',
        hi: 'दो सन्निकट केन्द्रों में समस्त ग्रहों के स्थित होने से गदा योग बनता है। यह जातक को साहसी, शस्त्र/शास्त्र में प्रवीण, धनवान तथा यज्ञशील बनाता है।'
      },
      result: { en: 'Steadfast valor, wealth through personal exertion, and formidable power in contentious enterprise.', hi: 'बाहुबल से धनार्जन, शास्त्र/रण कौशल तथा अजेय संकल्प।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isShakata) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 7], kundali, 52);
    yogas.push({
      id: 'nabhasa_akriti_shakata',
      name: 'Shakata Yoga (Akriti)',
      sanskritName: 'शकट योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy exclusively the 1st (Lagna) and 7th Kendras.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 7],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 10',
      shlokaReference: 'लग्नेऽस्ते च स्थिताः सर्वे शकटो नाम जायते। गदरोगी नरो मूर्खश्चक्रसेवी च जायते॥',
      description: {
        en: 'Shakata (Cart) yoga forms when planets are aligned along the Ascendant-Descendant axis, indicating periodic financial fluctuations, laborious enterprise, and endurance.',
        hi: 'लग्न एवं सप्तम केन्द्र में समस्त ग्रहों के होने से शकट योग बनता है। यह जीवन में उतार-चढ़ाव, श्रमसाध्य मार्ग एवं दीर्घकालिक सहनशक्ति का सूचक है।'
      },
      result: { en: 'Endurance through cyclic changes, commercial mobility, and ultimate fortitude.', hi: 'चक्रीय परिवर्तन, निरंतर श्रम तथा उत्तरार्ध में आत्मनिर्भरता।' },
      isAuspicious: false,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isVihaga) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [4, 10], kundali, 56);
    yogas.push({
      id: 'nabhasa_akriti_vihaga',
      name: 'Vihaga Yoga (Akriti)',
      sanskritName: 'विहग योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy exclusively the 4th (Sukha) and 10th (Karma) Kendras.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [4, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 10',
      shlokaReference: 'सुखाम्बरगतैः खेटैर्विहगो जायते ततः। प्रवासरुचिरत्यर्थं दूतकार्यपरायणः॥',
      description: {
        en: 'Vihaga (Bird) yoga forms when planets reside along the Nadir-Midheaven axis, bestowing love for travel, roving commissions, ambassadorial roles, and swift execution.',
        hi: 'चतुर्थ व दशम भाव में ग्रहों के स्थित होने से विहग योग बनता है। यह जातक को देशाटन प्रिय, दूत कार्य (राजदूत/प्रतिनिधि) तथा गतिशील बनाता है।'
      },
      result: { en: 'Diplomatic dexterity, frequent travel, and professional mobility across foreign territories.', hi: 'यात्राओं द्वारा भाग्योदय, मध्यस्थता कौशल तथा विस्तृत संपर्क।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isShringataka) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 5, 9], kundali, 75);
    yogas.push({
      id: 'nabhasa_akriti_shringataka',
      name: 'Shringataka Yoga (Akriti)',
      sanskritName: 'शृङ्गाटक योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets reside exclusively in Dharma Trikonas (Houses 1, 5, 9).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 5, 9],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 13',
      shlokaReference: 'लग्ने धर्मे सुते चैव सर्वे स्युर्यदि खेचराः। शृङ्गाटकाभिधो योगो रणे शूरः प्रियो नृणाम्॥',
      description: {
        en: 'Shringataka yoga forms when all grahas gather in the triangle of Dharma, endowing extraordinary wisdom, spiritual integrity, and noble valor.',
        hi: 'त्रिकोण भावों (१, ५, ९) में समस्त ग्रहों के होने से शृङ्गाटक योग बनता है। यह उच्च धर्मपरायणता तथा विद्या में श्रेष्ठता देता है।'
      },
      result: { en: 'High scholastic standing, auspicious progeny, and spiritual protection.', hi: 'विद्या, धर्म तथा सत्कर्मों द्वारा प्रचुर यश।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isHala) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 60);
    yogas.push({
      id: 'nabhasa_akriti_hala',
      name: 'Hala Yoga (Akriti)',
      sanskritName: 'हल योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets reside exclusively in trikona houses starting from 2nd, 3rd, or 4th (2-6-10, 3-7-11, or 4-8-12).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 13',
      shlokaReference: 'अन्यानि तु त्रिकोणानि हलयोगः प्रकीर्तितः। कृषिकार्यपरो नित्यं बहुभुग्धनधान्यवान्॥',
      description: {
        en: 'Hala (Plough) yoga forms when planets assemble across non-dharma triangular trines, giving industrious agrarian wealth, persistent labor, and tangible material yields.',
        hi: 'धर्म त्रिकोण से भिन्न त्रिकोणों (२-६-१०, ३-७-११ या ४-८-१२) में ग्रह होने पर हल योग बनता है। यह कृषि, भूमि तथा कठोर परिश्रम से धनोपार्जन देता है।'
      },
      result: { en: 'Abundant land holdings, productive craftsmanship, and self-made financial solidity.', hi: 'कृषि व भूमि से लाभ, स्वावलंबन तथा संचित भौतिक सम्पदा।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isVajra) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 4, 7, 10], kundali, 72);
    yogas.push({
      id: 'nabhasa_akriti_vajra',
      name: 'Vajra Yoga (Akriti)',
      sanskritName: 'वज्र योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Benefic planets occupy 1st & 7th Kendras, while malefic planets occupy 4th & 10th Kendras.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 4, 7, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 14',
      shlokaReference: 'लग्ने द्यूने स्थिताः सौम्याः पापा बन्धौ नभस्थले। योगो वज्राभिधो ज्ञेयः सुखधैर्यसमन्वितः॥',
      description: {
        en: 'Vajra (Thunderbolt) yoga forms when benefics illuminate the horizontal axis while malefics fortify the vertical axis, granting courage, beauty, and indomitable grit.',
        hi: 'लग्न व सप्तम में शुभ ग्रह तथा चतुर्थ व दशम में पाप ग्रह होने से वज्र योग बनता है। यह धैर्य, रूप तथा पराक्रम प्रदान करता है।'
      },
      result: { en: 'Resilient leadership, physical attractiveness, and formidable endurance in public life.', hi: 'अद्वितीय धैर्य, सुन्दर स्वरूप तथा जनमानस में सम्मान।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isYava) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 4, 7, 10], kundali, 66);
    yogas.push({
      id: 'nabhasa_akriti_yava',
      name: 'Yava Yoga (Akriti)',
      sanskritName: 'यव योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Malefic planets occupy 1st & 7th Kendras, while benefic planets occupy 4th & 10th Kendras.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 4, 7, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 14',
      shlokaReference: 'व्यत्ययेन यवो ज्ञेयो वज्राकारफलो हि सः। व्रताचारपरो नित्यं मध्यवयसि सुखान्वितः॥',
      description: {
        en: 'Yava (Barley-grain) yoga forms when malefics occupy 1st and 7th while benefics occupy 4th and 10th, conferring pious discipline, steady charity, and flourishing middle age.',
        hi: 'लग्न व सप्तम में पाप ग्रह तथा चतुर्थ व दशम में शुभ ग्रह होने से यव योग बनता है। यह अनुशासित जीवन तथा मध्यायु में प्रचुर समृद्धि देता है।'
      },
      result: { en: 'Devout charity, rigorous discipline, and prosperous comfort during middle and later life.', hi: 'धार्मिक निष्ठा, संयम तथा जीवन के मध्य काल में सुख-समृद्धि।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isKamala) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 4, 7, 10], kundali, 80);
    yogas.push({
      id: 'nabhasa_akriti_kamala',
      name: 'Kamala Yoga (Akriti)',
      sanskritName: 'कमल योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_APAVADA_RESIDUAL',
      formationRule: 'All seven classical planets reside across all four angular Kendra houses (1, 4, 7, 10).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 4, 7, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 15',
      shlokaReference: 'चतुर्षु केन्द्रेषु यदा सर्वे तिष्ठन्ति खेचराः। कमलो नाम योगोऽयं विख्यातो नृपवल्लभः॥',
      description: {
        en: 'Kamala (Lotus) yoga is an exalted planetary formation wherein all planets radiate from the cardinal angles, endowing spotless character, renown, and sovereign honor.',
        hi: 'चारों केन्द्रों में समस्त ग्रहों के स्थित होने से कमल योग बनता है। यह निष्कलंक कीर्ति, प्रभुत्व तथा दीर्घायु प्रदान करता है।'
      },
      result: { en: 'Widespread fame, virtuous authority, wealth of character, and enduring majesty.', hi: 'निर्मल यश, उच्च प्रतिष्ठा तथा सर्वजनप्रिय व्यक्तित्व।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isVapi) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 66);
    yogas.push({
      id: 'nabhasa_akriti_vapi',
      name: 'Vapi Yoga (Akriti)',
      sanskritName: 'वापी योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'SCHOLARLY_INTERPRETATIVE',
      formationRule: 'All seven classical planets reside exclusively in non-kendra houses (Panapharas 2, 5, 8, 11 or Apoklimas 3, 6, 9, 12).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 15',
      shlokaReference: 'केन्द्रादन्यत्र संस्थिताः वापी योगः प्रकीर्तितः। चिरसञ्चितवित्तोऽपि निधिलाभो भवेन्नृणाम्॥',
      description: {
        en: 'Vapi (Water Reservoir) yoga forms when planets reside purely in succession houses outside angles, conferring prudent asset accumulation, subterranean wealth, and durable comfort.',
        hi: 'केन्द्रों से बाहर (पणफर अथवा आपोक्लिम) भावों में समस्त ग्रहों के होने से वापी योग बनता है। यह जातक को गुप्त धन, संचित संपदा तथा स्थायी सुख देता है।'
      },
      result: { en: 'Prudent accumulation of reserves, enduring real estate assets, and financial peace.', hi: 'संचित धन, स्थायी कोष तथा जीवन पर्यन्त आर्थिक स्थिरता।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isYupam) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [1, 2, 3, 4], kundali, 68);
    yogas.push({
      id: 'nabhasa_akriti_yupam',
      name: 'Yupam Yoga (Akriti)',
      sanskritName: 'यूप योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy four continuous houses starting from Lagna (1, 2, 3, 4).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [1, 2, 3, 4],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 11',
      shlokaReference: 'लग्नाच्चतुर्षु भावेषु यूपयोगः प्रकीर्तितः। व्रताचारपरो विद्वान् यज्वैश्वर्यसमन्वितः॥',
      description: {
        en: 'Yupam (Sacrificial Post) yoga forms when planets occupy houses 1 through 4 continuously, bestowing mastery in self-control, spiritual vows, hospitality, and celebrated virtues.',
        hi: 'लग्न से चतुर्थ भाव तक लगातार चार भावों में ग्रह होने से यूप योग बनता है। यह संयम, सत्यपरायणता, ज्ञान तथा आत्म-नियंत्रण देता है।'
      },
      result: { en: 'Scholastic renown, unswerving moral uprightness, and spiritual authority.', hi: 'सद्गुण, विद्वत्ता तथा समाज में प्रतिष्ठित सदाचार।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isShara) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [4, 5, 6, 7], kundali, 64);
    yogas.push({
      id: 'nabhasa_akriti_shara',
      name: 'Shara (Bāna) Yoga (Akriti)',
      sanskritName: 'शर (बाण) योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy four continuous houses starting from 4th (4, 5, 6, 7).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [4, 5, 6, 7],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 11',
      shlokaReference: 'सुखात् शरः प्रदिष्टश्च शस्त्रकृच्छूरनायकः।',
      description: {
        en: 'Shara (Arrow) yoga forms when planets span houses 4 through 7 continuously, endowing incisive sharp intellect, military engineering prowess, and focused martial skill.',
        hi: 'चतुर्थ से सप्तम भाव तक लगातार चार भावों में ग्रह होने से शर योग बनता है। यह अस्त्र-शस्त्र निर्माण, तीक्ष्ण बुद्धि तथा साहसी नेतृत्व देता है।'
      },
      result: { en: 'Technical acumen, martial strategy, and targeted commercial execution.', hi: 'तकनीकी दक्षता, शस्त्र/यंत्र संचालन तथा साहसिक उद्यम।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isShakti) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [7, 8, 9, 10], kundali, 62);
    yogas.push({
      id: 'nabhasa_akriti_shakti',
      name: 'Shakti Yoga (Akriti)',
      sanskritName: 'शक्ति योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy four continuous houses starting from 7th (7, 8, 9, 10).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [7, 8, 9, 10],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 11',
      shlokaReference: 'द्यूनाच्छक्तिरिति स्मृतः। युद्धवीरो जयप्राप्तः क्रियादक्षो महाबली॥',
      description: {
        en: 'Shakti (Spear) yoga forms when planets span houses 7 through 10 continuously, imparting aggressive stamina, tactical command, and triumph in competitive campaigns.',
        hi: 'सप्तम से दशम भाव तक लगातार चार भावों में ग्रह होने से शक्ति योग बनता है। यह जातक को युद्धवीर, क्रियाशील तथा विजयशील बनाता है।'
      },
      result: { en: 'Dominance in competitive fields, organizational command, and vigorous stamina.', hi: 'प्रतिस्पर्धा में विजय, साहसिक निर्णय तथा प्रशासनिक शक्ति।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isDanda) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, [10, 11, 12, 1], kundali, 65);
    yogas.push({
      id: 'nabhasa_akriti_danda',
      name: 'Danda Yoga (Akriti)',
      sanskritName: 'दण्ड योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy four continuous houses starting from 10th (10, 11, 12, 1).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: [10, 11, 12, 1],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 11',
      shlokaReference: 'दण्डः स्यात् खस्थितेषु च। दण्डनायकविख्यातो नीतिमान् न्यायकोविदः॥',
      description: {
        en: 'Danda (Staff of Justice) yoga forms when planets span houses 10 through 1 continuously, conferring judicial authority, magisterial stature, and strict legal rectitude.',
        hi: 'दशम से लग्न तक लगातार चार भावों में ग्रह होने से दण्ड योग बनता है। यह जातक को न्यायप्रिय, दण्डाधिकारी तथा नीतिज्ञ बनाता है।'
      },
      result: { en: 'Executive jurisdiction, legal acumen, and respected penal authority.', hi: 'न्यायिक प्रतिष्ठा, नीति कुशलता तथा अनुशासनप्रियता।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isNauka) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 70);
    yogas.push({
      id: 'nabhasa_akriti_nauka',
      name: 'Nauka Yoga (Akriti)',
      sanskritName: 'नौका योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy seven continuous houses starting from Lagna (1 through 7).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 12',
      shlokaReference: 'लग्नात् सप्तसु भावेषु नौकायोगः प्रकीर्तितः। जलयानेन लाभश्च ख्यातिमान् धनवान् भवेत्॥',
      description: {
        en: 'Nauka (Boat) yoga forms when planets span houses 1 through 7, indicating gains through sea trade, transport, international logistics, and public acclaim.',
        hi: 'लग्न से सप्तम भाव तक लगातार सात भावों में ग्रह होने से नौका योग बनता है। यह जलमार्ग, व्यापार तथा यात्राओं से प्रचुर लाभ देता है।'
      },
      result: { en: 'Commercial prosperity through trade networks, travel, and expansive ventures.', hi: 'व्यापारिक संजाल, जल/परिवहन से लाभ तथा सार्वजनिक ख्याति।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isKutira) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 62);
    yogas.push({
      id: 'nabhasa_akriti_kutira',
      name: 'Kutira Yoga (Akriti)',
      sanskritName: 'कुटीर योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy seven continuous houses starting from 4th (4 through 10).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 12',
      shlokaReference: 'कुटीरस्तु सुखात् परम्। सत्यवादी तपस्वी च गृहनिर्माणतत्परः॥',
      description: {
        en: 'Kutira (Cottage) yoga forms when planets span houses 4 through 10, creating dedicated shelter builders, truth-tellers, hospitable souls, and rustic benefactors.',
        hi: 'चतुर्थ से दशम भाव तक सात लगातार भावों में ग्रह होने से कुटीर योग बनता है। यह सत्यवादी, परोपकारी तथा आवास निर्माण में कुशल बनाता है।'
      },
      result: { en: 'Integrity of word, construction of sanctuaries, and steadfast domestic contentment.', hi: 'सत्यनिष्ठा, गृह व आश्रय निर्माण तथा पारिवारिक संतोष।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isChhatra) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 74);
    yogas.push({
      id: 'nabhasa_akriti_chhatra',
      name: 'Chhatra Yoga (Akriti)',
      sanskritName: 'छत्र योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy seven continuous houses starting from 7th (7 through 1).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 12',
      shlokaReference: 'सप्तमाच्छत्रमुद्दिष्टं नृपपूज्यो महामतिः। स्वकुलस्य शिरोमणिः सुखवान् नित्यमुत्सवः॥',
      description: {
        en: 'Chhatra (Royal Parasol) yoga forms when planets span houses 7 through 1, granting sovereign protection, lineage pride, royal esteem, and joy in late life.',
        hi: 'सप्तम से लग्न तक सात लगातार भावों में ग्रह होने से छत्र योग बनता है। यह कुल का शिरोमणि, राजा से सम्मानित तथा सुख-सौभाग्यशाली बनाता है।'
      },
      result: { en: 'Sovereign patronage, family leadership, and serene luxury in the zenith of career.', hi: 'राजकीय संरक्षण, कुलदीपक पदवी तथा उत्तरार्ध में अखंड सुख।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isChapa) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 72);
    yogas.push({
      id: 'nabhasa_akriti_chapa',
      name: 'Chapa (Dhanus) Yoga (Akriti)',
      sanskritName: 'चाप (धनुष) योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy seven continuous houses starting from 10th (10 through 4).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 12',
      shlokaReference: 'दशमाच्चाप उच्यते। साहसी रणशूरश्च धनधान्यसमन्वितः॥',
      description: {
        en: 'Chapa (Bow) yoga forms when planets span houses 10 through 4, endowing ambitious trajectory, unyielding daring, military honor, and bountiful acquisitions.',
        hi: 'दशम से चतुर्थ भाव तक सात लगातार भावों में ग्रह होने से चाप योग बनता है। यह साहसी, पराक्रमी तथा धन-धान्य से परिपूर्ण बनाता है।'
      },
      result: { en: 'Bold enterprise, targeted ambition, and sovereign defense accomplishments.', hi: 'साहसिक कार्य, लक्ष्यवेधी पराक्रम तथा भौतिक संपन्नता।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isArdhaChandra) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 72);
    yogas.push({
      id: 'nabhasa_akriti_ardhachandra',
      name: 'Ardha-Chandra Yoga (Akriti)',
      sanskritName: 'अर्धचन्द्र योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets occupy seven continuous houses starting from a Panaphara or Apoklima house.',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 16',
      shlokaReference: 'पणफरात् प्रवृत्तानि ह्यापोक्लिमादथापि वा। सप्तागारेषु खेटैश्च योगोऽयमर्धचन्द्रकः॥',
      description: {
        en: 'Ardha-Chandra (Crescent Moon) yoga forms when planets span seven continuous houses commencing from a non-angular house, giving handsome elegance, military commander rank, and royal affection.',
        hi: 'पणफर अथवा आपोक्लिम भाव से प्रारम्भ होकर लगातार सात भावों में ग्रह होने से अर्धचन्द्र योग बनता है। यह सेनापति पद, सौन्दर्य तथा राजकृपा प्रदान करता है।'
      },
      result: { en: 'Command of executive forces, charming presence, and regal favor in society.', hi: 'सेना या संगठन का नेतृत्व, सम्मोहक व्यक्तित्व तथा सामाजिक सम्मान।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isChakra) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 76);
    yogas.push({
      id: 'nabhasa_akriti_chakra',
      name: 'Chakra Yoga (Akriti)',
      sanskritName: 'चक्र योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets reside across alternate odd houses (1, 3, 5, 7, 9, 11).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 17',
      shlokaReference: 'विषमेषु च भावेषु चक्रयोगः प्रकीर्तितः। चक्रवर्ती समो राजा सार्वभौमगुणान्वितः॥',
      description: {
        en: 'Chakra (Wheel of Sovereign Rule) yoga forms when planets alternate through odd houses, indicating universal renown, statesman authority, and majestic governance.',
        hi: 'विषम भावों (१, ३, ५, ७, ९, ११) में समस्त ग्रहों के होने से चक्र योग बनता है। यह चक्रवर्ती राजा के समान प्रभाव, कीर्ति तथा शासक गुण प्रदान करता है।'
      },
      result: { en: 'Sovereign statecraft, international influence, and unassailable authority.', hi: 'सार्वभौम कीर्ति, नीति निपुणता तथा सर्वोच्च नेतृत्व।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  if (isSamudra) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, 74);
    yogas.push({
      id: 'nabhasa_akriti_samudra',
      name: 'Samudra Yoga (Akriti)',
      sanskritName: 'समुद्र योग (आकृति)',
      category: 'NABHASA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'All seven classical planets reside across alternate even houses (2, 4, 6, 8, 10, 12).',
      participatingPlanets: SEVEN_CLASSICAL_PLANETS,
      participatingHouses: housesOccupied,
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 17',
      shlokaReference: 'समेषु च समुद्रः स्यात् रत्नाढ्यो बहुभाग्यवान्। परोपकारी विख्यातो भोगवान् सुखभाजनः॥',
      description: {
        en: 'Samudra (Ocean) yoga forms when planets alternate through even houses, endowing profound generosity, pearl-like riches, oceanic compassion, and constant enjoyment.',
        hi: 'सम भावों (२, ४, ६, ८, १०, १२) में समस्त ग्रहों के होने से समुद्र योग बनता है। यह रत्न, अकूत धन, परोपकार तथा भोग-विलास प्रदान करता है।'
      },
      result: { en: 'Vast reservoir of wealth, philanthropic generosity, and enduring luxury.', hi: 'अक्षय धन-सम्पदा, परोपकार तथा ऐश्वर्यशाली जीवन।' },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // D. Sankhya Yogas (Fallback by Sign Count - BPHS Ch. 35, Shlokas 21–24)
  // MANDATORY CLASSICAL PRECEDENCE RULE:
  // "आश्रयाद्या न सिध्यन्ति यदा योगा विशाम्पते। संख्यायोगास्तदा ज्ञेया नान्यथेति पराशरः॥"
  // Sankhya Yogas are evaluated ONLY when NO Ashraya, Dala, or Akriti Yoga is formed.
  const hasPrimaryNabhasa = yogas.length > 0;

  if (!hasPrimaryNabhasa) {
    const count = signsOccupied.length;
    const SANKHYA_MAP: Record<number, { id: string; name: string; sa: string; shloka: string; en: string; hi: string; score: number }> = {
      7: { id: 'nabhasa_sankhya_vallaki', name: 'Vallaki (Veena) Yoga', sa: 'वल्लकी (वीणा) योग', shloka: 'सप्तर्षिगे वल्लकि स्यात्।', en: 'Planets across 7 signs: gifted in fine arts, music, eloquence, and literature.', hi: '७ राशियों में ग्रह: संगीत, ललित कलाएं, काव्य तथा साहित्य में अद्वितीय रुचि।', score: 70 },
      6: { id: 'nabhasa_sankhya_dama', name: 'Dama Yoga', sa: 'दाम योग', shloka: 'षट्सु दामः प्रकीर्तितः।', en: 'Planets across 6 signs: disciplined, philanthropic, generous patron of society.', hi: '६ राशियों में ग्रह: संयमी, परोपकारी तथा समाज में प्रतिष्ठित दाता।', score: 68 },
      5: { id: 'nabhasa_sankhya_pasha', name: 'Pasha Yoga', sa: 'पाश योग', shloka: 'पञ्चसु पाशः।', en: 'Planets across 5 signs: skillful tactician, extensive social kinship, adept negotiator.', hi: '५ राशियों में ग्रह: चतुर नीतिज्ञ, कुटुंब व मित्रों का विस्तृत संजाल।', score: 65 },
      4: { id: 'nabhasa_sankhya_kedara', name: 'Kedara Yoga', sa: 'केदार योग', shloka: 'चतुर्षु केदारः।', en: 'Planets across 4 signs: grounded agriculturist, real-estate prosperity, patient builder.', hi: '४ राशियों में ग्रह: कृषि, भूमि लाभ, धैर्यवान तथा स्थायी संपदा का स्वामी।', score: 64 },
      3: { id: 'nabhasa_sankhya_shoola', name: 'Shoola Yoga', sa: 'शूल योग', shloka: 'त्रिषु शूलः।', en: 'Planets across 3 signs: sharp incisive spirit, courageous fighter, relentless resolve.', hi: '३ राशियों में ग्रह: तीक्ष्ण स्वभाव, साहसी तथा संघर्षों से न घबराने वाला।', score: 58 },
      2: { id: 'nabhasa_sankhya_yuga', name: 'Yuga Yoga', sa: 'युग योग', shloka: 'द्वयोर्युगः।', en: 'Planets concentrated in 2 signs: highly focused unconventional pursuits, intense polarity.', hi: '२ राशियों में ग्रह: गहन एकाग्रता, अपरंपरागत मार्ग तथा तीव्र कर्मठता।', score: 52 },
      1: { id: 'nabhasa_sankhya_gola', name: 'Gola Yoga', sa: 'गोल योग', shloka: 'एकस्मिन् गोल उच्यते।', en: 'All planets in a single sign: rare intense singular focus, extreme karmic destiny.', hi: '१ राशि में समस्त ग्रह: असाधारण केंद्रित ऊर्जा, गहन प्रारब्ध चक्र।', score: 50 }
    };

    const sankhya = SANKHYA_MAP[count];
    if (sankhya) {
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(sevenGrahas, housesOccupied, kundali, sankhya.score);
      yogas.push({
        id: sankhya.id,
        name: `${sankhya.name} (Sankhya)`,
        sanskritName: `${sankhya.sa} (संख्या)`,
        category: 'NABHASA',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `All seven classical planets reside across exactly ${count} zodiac signs.`,
        participatingPlanets: SEVEN_CLASSICAL_PLANETS,
        participatingHouses: housesOccupied,
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 35, Shloka 18–24',
        shlokaReference: sankhya.shloka,
        description: { en: sankhya.en, hi: sankhya.hi },
        result: { en: sankhya.en, hi: sankhya.hi },
        isAuspicious: count >= 4,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 2. PANCHA MAHAPURUSHA YOGAS EVALUATOR
// ----------------------------------------------------------------------------

export function evaluatePanchaMahapurushaYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const planets = kundali.planets;
  const KENDRA_HOUSES = [1, 4, 7, 10];

  const MAHAPURUSHA_CONFIGS: {
    id: string;
    planet: PlanetName;
    name: string;
    saName: string;
    shloka: string;
    ownSigns: number[];
    exaltSign: number;
    descEn: string;
    descHi: string;
    resEn: string;
    resHi: string;
  }[] = [
    {
      id: 'mahapurusha_ruchaka',
      planet: 'Mars',
      name: 'Ruchaka Yoga',
      saName: 'रुचक योग (पंच महापुरुष)',
      shloka: 'लग्नात् केन्द्रे स्वतुङ्गे वा भौमे रुचकलक्षणम्। शूरो राजा रणे धीरः सेनानीर्भूमिनायकः॥',
      ownSigns: [0, 7], // Aries, Scorpio
      exaltSign: 9,     // Capricorn
      descEn: 'Formed when Mars occupies an angular Kendra house (1, 4, 7, 10) in its Own Sign (Aries, Scorpio) or Exaltation (Capricorn). Bestows commanding physical courage, administrative grit, real estate prominence, and sovereign leadership.',
      descHi: 'मंगल के केन्द्र में स्वराशि (मेष, वृश्चिक) अथवा उच्च राशि (मकर) में स्थित होने से रुचक महापुरुष योग बनता है। यह शौर्य, पराक्रम, भूमि लाभ तथा निर्भीक नेतृत्व प्रदान करता है।',
      resEn: 'Executive authority, land ownership, competitive supremacy, and commanding vigor.',
      resHi: 'प्रशासनिक पद, अचल संपत्ति, सेना/प्रबंधन में उच्च पद तथा अदम्य साहस।'
    },
    {
      id: 'mahapurusha_bhadra',
      planet: 'Mercury',
      name: 'Bhadra Yoga',
      saName: 'भद्र योग (पंच महापुरुष)',
      shloka: 'स्वक्षेत्रे यदि वा तुङ्गे केन्द्रे सौम्ये तु भद्रकः। विद्वान् विप्रो मतिश्रेष्ठो दीर्घायुर्वाक्पतिः समः॥',
      ownSigns: [2, 5], // Gemini, Virgo
      exaltSign: 5,     // Virgo
      descEn: 'Formed when Mercury occupies an angular Kendra house in Gemini or Virgo. Confers supreme intellect, commercial genius, razor-sharp analytical clarity, and oratorical brilliance.',
      descHi: 'बुध के केन्द्र में स्वराशि अथवा उच्च राशि (मिथुन, कन्या) में होने से भद्र महापुरुष योग बनता है। यह जातक को महाबुद्धिमान, कुशल वक्ता तथा व्यापार-नीतिज्ञ बनाता है।',
      resEn: 'Commercial fortune, academic acclaim, advisory stature, and intellectual eloquence.',
      resHi: 'वाणिज्यिक सफलता, लेखन-भाषण कला में प्रवीणता तथा तीक्ष्ण मेधा।'
    },
    {
      id: 'mahapurusha_hamsa',
      planet: 'Jupiter',
      name: 'Hamsa Yoga',
      saName: 'हंस योग (पंच महापुरुष)',
      shloka: 'लग्नात् केन्द्रे स्वतुङ्गे वा गुरौ हंसः प्रकीर्तितः। धर्मात्मा गुणवान् विप्रो राजपूज्यो जितेन्द्रियः॥',
      ownSigns: [8, 11], // Sagittarius, Pisces
      exaltSign: 3,      // Cancer
      descEn: 'Formed when Jupiter occupies an angular Kendra house in Sagittarius, Pisces, or Cancer. Bestows noble ethical majesty, spiritual wisdom, university preceptor status, and universal reverence.',
      descHi: 'गुरु के केन्द्र में स्वराशि (धनु, मीन) या उच्च राशि (कर्क) में होने से हंस महापुरुष योग बनता है। यह जातक को धर्मात्मा, ज्ञानी, नीतिपरायण तथा सर्वपूज्य गुरु पदवी देता है।',
      resEn: 'Revered advisory roles, moral sovereignty, intellectual legacy, and divine grace.',
      resHi: 'उच्च आध्यात्मिक ज्ञान, राज्य सम्मान, सद्गुण तथा सर्वमान्य प्रतिष्ठा।'
    },
    {
      id: 'mahapurusha_malavya',
      planet: 'Venus',
      name: 'Malavya Yoga',
      saName: 'मालव्य योग (पंच महापुरुष)',
      shloka: 'स्वक्षेत्रे यदि वा तुङ्गे केन्द्रे दैत्यगुरौ स्थिते। मालव्यनाम योगोऽयं भोगवान् नृपसम्मतः॥',
      ownSigns: [1, 6], // Taurus, Libra
      exaltSign: 11,    // Pisces
      descEn: 'Formed when Venus occupies an angular Kendra house in Taurus, Libra, or Pisces. Confers refined aesthetic opulence, artistic fame, luxury conveyances, and magnetic charisma.',
      descHi: 'शुक्र के केन्द्र में स्वराशि (वृषभ, तुला) या उच्च राशि (मीन) में स्थित होने से मालव्य महापुरुष योग बनता है। यह ऐश्वर्य, कलात्मक सुरुचि, वाहन सुख तथा वैभवशाली जीवन देता है।',
      resEn: 'Luxury conveyances, diplomatic charm, artistic eminence, and marital joy.',
      resHi: 'भौतिक सुख-सुविधाएं, वाहन सुख, कला-सौन्दर्य में ख्याति तथा ऐश्वर्य।'
    },
    {
      id: 'mahapurusha_sasa',
      planet: 'Saturn',
      name: 'Sasa Yoga',
      saName: 'शश योग (पंच महापुरुष)',
      shloka: 'केन्द्रे शनावनल्पे वा स्वतुङ्गे शशलक्षणम्। प्रधानो ग्रामदण्डाख्यो न्यायविद् भूधराश्रयः॥',
      ownSigns: [9, 10], // Capricorn, Aquarius
      exaltSign: 6,      // Libra
      descEn: 'Formed when Saturn occupies an angular Kendra house in Capricorn, Aquarius, or Libra. Endows the native with deep judicial wisdom, organizational patience, grassroots mass leadership, and enduring governance.',
      descHi: 'शनि के केन्द्र में स्वराशि (मकर, कुंभ) या उच्च राशि (तुला) में स्थित होने से शश महापुरुष योग बनता है। यह जातक को जननायक, न्यायप्रिय, धीर-गंभीर प्रशासक बनाता है।',
      resEn: 'Grassroots authority, institutional governance, perseverance through trials, and lasting fame.',
      resHi: 'जनसमर्थन, संगठन क्षमता, न्यायिक पद तथा दीर्घकालिक स्थिर प्रभाव।'
    }
  ];

  for (const cfg of MAHAPURUSHA_CONFIGS) {
    const pl = planets.find(p => p.name === cfg.planet);
    if (!pl) continue;

    const inKendra = KENDRA_HOUSES.includes(pl.house);
    const inEligibleSign = cfg.ownSigns.includes(pl.signIndex) || pl.signIndex === cfg.exaltSign;

    if (inKendra && inEligibleSign) {
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [pl],
        [pl.house],
        kundali,
        82
      );

      yogas.push({
        id: cfg.id,
        name: cfg.name,
        sanskritName: cfg.saName,
        category: 'MAHAPURUSHA',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `${cfg.planet} occupies Kendra house ${pl.house} in its own or exalted sign (${pl.sign}).`,
        participatingPlanets: [cfg.planet],
        participatingHouses: [pl.house],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 75 / Phaladeepika, Ch. 6, Shloka 1–4',
        shlokaReference: cfg.shloka,
        description: { en: cfg.descEn, hi: cfg.descHi },
        result: { en: cfg.resEn, hi: cfg.resHi },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 3. CLASSICAL SOLAR & LUNAR YOGAS EVALUATOR
// ----------------------------------------------------------------------------

export function evaluateSolarLunarYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const planets = kundali.planets;
  const sun = planets.find(p => p.name === 'Sun');
  const moon = planets.find(p => p.name === 'Moon');
  if (!sun || !moon) return yogas;

  const ascSign = planets.find(p => p.name === 'Ascendant')?.signIndex ?? 0;

  // A. Lunar Yogas (Sunapha, Anapha, Durdhara, Kemadruma)
  // Non-lunar, non-solar, non-nodal planets: Mars, Mercury, Jupiter, Venus, Saturn
  const LUNAR_QUALIFIED: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const planetsIn2ndFromMoon = planets.filter(p => LUNAR_QUALIFIED.includes(p.name as PlanetName) && getHouseDifference(p.house, moon.house) === 2);
  const planetsIn12thFromMoon = planets.filter(p => LUNAR_QUALIFIED.includes(p.name as PlanetName) && getHouseDifference(p.house, moon.house) === 12);

  if (planetsIn2ndFromMoon.length > 0 && planetsIn12thFromMoon.length > 0) {
    const participants = [...planetsIn2ndFromMoon, ...planetsIn12thFromMoon];
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(participants, [moon.house], kundali, 75);
    yogas.push({
      id: 'lunar_durdhara',
      name: 'Durdhara Yoga (Lunar)',
      sanskritName: 'दुरुधरा योग (चन्द्र योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Sun, Rahu, and Ketu occupy both the 2nd and 12th houses from Moon.',
      participatingPlanets: ['Moon', ...participants.map(p => p.name as PlanetName)],
      participatingHouses: [moon.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 3',
      shlokaReference: 'उभयतः दुरुधरा स्यात्। भोगवान् धनसम्पन्नो मतिमान् ख्यातपौरुषः॥',
      description: {
        en: 'Durdhara yoga forms when the Moon is sheltered on both flanks by planets, conferring abundant wealth, conveyances, social popularity, and balanced psychological resilience.',
        hi: 'चन्द्रमा के दोनों ओर (द्वितीय एवं द्वादश भाव) ग्रह स्थित होने से दुरुधरा योग बनता है। यह सुख, समृद्धि, वाहन तथा संतुलित मानसिक बल प्रदान करता है।'
      },
      result: {
        en: 'Generous livelihood, societal respect, domestic joy, and emotional equilibrium.',
        hi: 'समृद्ध जीवन, सामाजिक सम्मान तथा संतुलित मानसिक शांति।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (planetsIn2ndFromMoon.length > 0) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(planetsIn2ndFromMoon, [moon.house], kundali, 70);
    yogas.push({
      id: 'lunar_sunapha',
      name: 'Sunapha Yoga (Lunar)',
      sanskritName: 'सुनफा योग (चन्द्र योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Sun/Nodes occupy the 2nd house from Moon.',
      participatingPlanets: ['Moon', ...planetsIn2ndFromMoon.map(p => p.name as PlanetName)],
      participatingHouses: [moon.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 1',
      shlokaReference: 'द्वितीये सुनफा प्रोक्ता। स्वभुजोपार्जितवित्तो बुद्धिमान् कीर्तिमान् नरः॥',
      description: {
        en: 'Sunapha yoga occurs when planets occupy the 2nd house from the Moon, indicating self-earned prosperity, wise speech, intellectual clarity, and steady status.',
        hi: 'चन्द्रमा से द्वितीय भाव में ग्रह होने से सुनफा योग बनता है। यह स्वप्रयत्नों से धनार्जन, मेधा तथा सुकीर्ति प्रदान करता है।'
      },
      result: {
        en: 'Self-acquired affluence, scholarly disposition, and comfortable sustenance.',
        hi: 'स्वोपार्जित धन, बौद्धिक ख्याति तथा सुखमय आजीविका।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (planetsIn12thFromMoon.length > 0) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(planetsIn12thFromMoon, [moon.house], kundali, 68);
    yogas.push({
      id: 'lunar_anapha',
      name: 'Anapha Yoga (Lunar)',
      sanskritName: 'अनफा योग (चन्द्र योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Sun/Nodes occupy the 12th house from Moon.',
      participatingPlanets: ['Moon', ...planetsIn12thFromMoon.map(p => p.name as PlanetName)],
      participatingHouses: [moon.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 2',
      shlokaReference: 'द्वादशेऽनफा। सुशीलो नीतिसम्पन्नो नीरोगो जितमानसः॥',
      description: {
        en: 'Anapha yoga forms when planets occupy the 12th from Moon, endowing noble temperament, freedom from chronic physical distress, generous expenditure, and serene disposition.',
        hi: 'चन्द्रमा से द्वादश भाव में ग्रह होने से अनफा योग बनता है। यह जातक को सुशील, निरोगी, नीतिवान तथा उदार हृदय बनाता है।'
      },
      result: {
        en: 'Physical vitality, moral elegance, generous spirit, and peaceful legacy.',
        hi: 'आरोग्य, शीलवान स्वभाव तथा शांत चित्तवृत्ति।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else {
    // Kemadruma check: No planets in 2nd or 12th from Moon
    // Classical cancellation: Benefics in Kendra from Lagna or Moon, or Moon conjunct a planet
    const beneficsInKendraFromLagna = planets.filter(p => NATURAL_BENEFICS.includes(p.name as PlanetName) && [1, 4, 7, 10].includes(p.house));
    const planetsInKendraFromMoon = planets.filter(p => p.name !== 'Moon' && p.name !== 'Rahu' && p.name !== 'Ketu' && [1, 4, 7, 10].includes(getHouseDifference(p.house, moon.house)));
    const moonConjoined = planets.filter(p => p.name !== 'Moon' && p.name !== 'Ascendant' && p.house === moon.house);

    const isCancelled = beneficsInKendraFromLagna.length > 0 || planetsInKendraFromMoon.length > 0 || moonConjoined.length > 0;

    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics([moon], [moon.house], kundali, isCancelled ? 55 : 35);
    yogas.push({
      id: 'lunar_kemadruma',
      name: isCancelled ? 'Kemadruma Bhanga (Mitigated)' : 'Kemadruma Yoga',
      sanskritName: isCancelled ? 'केमद्रुम भंग योग (परिहार)' : 'केमद्रुम योग',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: isCancelled
        ? 'No planets in 2nd/12th from Moon, but classical mitigation applies via angular placement.'
        : 'No planets reside in 2nd or 12th house from Moon.',
      participatingPlanets: ['Moon'],
      participatingHouses: [moon.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 11–13',
      shlokaReference: 'केन्द्रे शशाङ्कात् यदि वा विलग्नात् शुभैश्च दृष्टे न च केमद्रुमः स्यात्।',
      description: {
        en: isCancelled
          ? 'Isolated Moon flanked by vacant houses, but neutralized by planets in angular Kendras from Lagna or Moon. The native experiences solitary mental phases that transform into high resilience.'
          : 'Moon sits unaccompanied in adjacent houses. Represents introspective loneliness, intermittent fluctuations, and deep inner self-reliance.',
        hi: isCancelled
          ? 'केन्द्र में शुभ ग्रहों के होने से केमद्रुम दोष का परिहार हो जाता है। यह जातक को एकांत प्रियता के उपरांत सशक्त आत्मनिर्भरता देता है।'
          : 'चन्द्रमा के आगे-पीछे ग्रह न होने से केमद्रुम योग बनता है। यह मानसिक अकेलापन तथा आत्मनिर्भरता का सूचक है।'
      },
      result: {
        en: isCancelled ? 'Temporary early isolation replaced by self-made security.' : 'Requires cultivation of emotional equanimity and grounding routines.',
        hi: isCancelled ? 'दोष भंग होने से आत्मनिर्भर सफलता।' : 'मानसिक शांति व स्थिरता के लिए नियमित साधना आवश्यक।'
      },
      isAuspicious: isCancelled,
      strengthScore,
      fruitionStatus: isCancelled ? 'MODERATE_POTENCY' : 'AFFLICTED_OR_DORMANT',
      qualifyingFactors: isCancelled ? ['Benefic in Kendra neutralizes isolation'] : qualifying,
      cancellationOrAfflictionFactors: isCancelled
        ? ['Classical Kendra mitigation: Isolated Moon cancelled by angular placement']
        : ['Unaccompanied Moon requires conscious emotional grounding']
    });
  }

  // B. Solar Yogas (Veshi, Voshi, Ubhayachari)
  const SOLAR_QUALIFIED: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const planetsIn2ndFromSun = planets.filter(p => SOLAR_QUALIFIED.includes(p.name as PlanetName) && getHouseDifference(p.house, sun.house) === 2);
  const planetsIn12thFromSun = planets.filter(p => SOLAR_QUALIFIED.includes(p.name as PlanetName) && getHouseDifference(p.house, sun.house) === 12);

  if (planetsIn2ndFromSun.length > 0 && planetsIn12thFromSun.length > 0) {
    const participants = [...planetsIn2ndFromSun, ...planetsIn12thFromSun];
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(participants, [sun.house], kundali, 74);
    yogas.push({
      id: 'solar_ubhayachari',
      name: 'Ubhayachari Yoga (Solar)',
      sanskritName: 'उभयचरी योग (सूर्य योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Moon/Nodes reside in both 2nd and 12th houses from Sun.',
      participatingPlanets: ['Sun', ...participants.map(p => p.name as PlanetName)],
      participatingHouses: [sun.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 8',
      shlokaReference: 'उभयतः उभयचरी। राजा वा राजतुल्यो वा महाभाग्यसमन्वितः॥',
      description: {
        en: 'Ubhayachari forms when the solar core is flanked on both sides by planets, bestowing royal favor, dignified presence, eloquent speech, and steady executive stature.',
        hi: 'सूर्य के दोनों ओर ग्रह होने से उभयचरी योग बनता है। यह जातक को तेजस्वी, राजमान्य तथा उच्च पद प्रतिष्ठा से संपन्न बनाता है।'
      },
      result: {
        en: 'State honors, dignified administrative reputation, and commanding presence.',
        hi: 'राजकीय सम्मान, तेजस्वी व्यक्तित्व तथा दीर्घकालिक मान-प्रतिष्ठा।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (planetsIn2ndFromSun.length > 0) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(planetsIn2ndFromSun, [sun.house], kundali, 68);
    yogas.push({
      id: 'solar_veshi',
      name: 'Veshi Yoga (Solar)',
      sanskritName: 'वेशि योग (सूर्य योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Moon/Nodes occupy the 2nd house from Sun.',
      participatingPlanets: ['Sun', ...planetsIn2ndFromSun.map(p => p.name as PlanetName)],
      participatingHouses: [sun.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 6',
      shlokaReference: 'द्वितीये वेशि योगः। धीरो धनसमृद्धश्च सत्यवादी नरो भवेत्॥',
      description: {
        en: 'Veshi forms when planets grace the 2nd house from the Sun, indicating truthful speech, executive patience, memory retention, and steady wealth acquisition.',
        hi: 'सूर्य से द्वितीय भाव में ग्रह होने से वेशि योग बनता है। यह जातक को सत्यवादी, धीर तथा आर्थिक रूप से सुदृढ़ बनाता है।'
      },
      result: {
        en: 'Financial security, articulate expression, and honorable reputation.',
        hi: 'आर्थिक स्थिरता, सत्यप्रिय वाणी तथा समाज में आदर।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  } else if (planetsIn12thFromSun.length > 0) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(planetsIn12thFromSun, [sun.house], kundali, 66);
    yogas.push({
      id: 'solar_voshi',
      name: 'Voshi Yoga (Solar)',
      sanskritName: 'वोशि योग (सूर्य योग)',
      category: 'SOLAR_LUNAR',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: 'Planets other than Moon/Nodes occupy the 12th house from Sun.',
      participatingPlanets: ['Sun', ...planetsIn12thFromSun.map(p => p.name as PlanetName)],
      participatingHouses: [sun.house],
      classicalSource: 'Brihat Parashara Hora Shastra, Ch. 37, Shloka 7',
      shlokaReference: 'द्वादशे वोशि योगः। बहुश्रुतो मतिमांश्च विख्यातकीर्तिर्गुणवान्॥',
      description: {
        en: 'Voshi forms when planets occupy the 12th from Sun, endowing deep learning, keen memory, charitable disposition, and quiet spiritual dignity.',
        hi: 'सूर्य से द्वादश भाव में ग्रह होने से वोशि योग बनता है। यह जातक को ज्ञानी, परोपकारी तथा आंतरिक तेज से युक्त बनाता है।'
      },
      result: {
        en: 'Spiritual inclination, deep insight, and generous societal contributions.',
        hi: 'विद्वत्ता, परोपकार तथा सम्मानित जीवन।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 4. CLASSICAL AUSPICIOUS YOGAS (Gajakesari, Budhaditya, Lakshmi, Saraswati, Amala)
// ----------------------------------------------------------------------------

export function evaluateClassicalGrahaYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const planets = kundali.planets;
  const sun = planets.find(p => p.name === 'Sun');
  const moon = planets.find(p => p.name === 'Moon');
  const mercury = planets.find(p => p.name === 'Mercury');
  const jupiter = planets.find(p => p.name === 'Jupiter');
  const venus = planets.find(p => p.name === 'Venus');
  const ascSign = planets.find(p => p.name === 'Ascendant')?.signIndex ?? 0;

  // A. Gaja Kesari Yoga (Jupiter in Kendra from Moon)
  if (jupiter && moon) {
    const distFromMoon = getHouseDifference(jupiter.house, moon.house);
    const inKendraFromMoon = [1, 4, 7, 10].includes(distFromMoon);

    if (inKendraFromMoon) {
      const isJupDeb = jupiter.signIndex === DEBILITATION_SIGNS['Jupiter'];
      const isMoonDeb = moon.signIndex === DEBILITATION_SIGNS['Moon'];
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [jupiter, moon],
        [jupiter.house, moon.house],
        kundali,
        isJupDeb || isMoonDeb ? 50 : 85
      );

      yogas.push({
        id: 'classical_gajakesari',
        name: 'Gaja Kesari Yoga',
        sanskritName: 'गजकेसरी योग',
        category: 'CLASSICAL',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `Jupiter is in angular Kendra (${distFromMoon}th house) from Moon.`,
        participatingPlanets: ['Jupiter', 'Moon'],
        participatingHouses: [jupiter.house, moon.house],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 36, Shloka 1 / Phaladeepika, Ch. 6',
        shlokaReference: 'केन्द्रे देवगुरौ चन्द्राद् योगोऽयं गजकेसरी। दिग्गजोपमविख्यातो नृपमान्यो महामतिः॥',
        description: {
          en: 'Formed when Jupiter is angular from the Moon. Bestows majestic lion-like valor (Kesari) paired with elephant-like enduring dignity (Gaja), scholarly intellect, enduring reputation, and virtue.',
          hi: 'चन्द्रमा से केन्द्र में देवगुरु बृहस्पति के होने पर गजकेसरी योग का निर्माण होता है। यह जातक को सिंह के समान तेजस्वी तथा गज के समान अचल ऐश्वर्य व मान-सम्मान देता है।'
        },
        result: {
          en: 'Administrative acumen, academic honor, generational legacy, and protection against sudden downfalls.',
          hi: 'उच्च विद्या, समाज में आदर, नीतिवान व्यक्तित्व तथा अखंड यश।'
        },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  // B. Budhaditya Yoga (Sun-Mercury conjunction without deep combustion)
  if (sun && mercury && sun.signIndex === mercury.signIndex) {
    const diffDeg = Math.abs(sun.degreeInSign - mercury.degreeInSign);
    const isCombust = mercury.isCombust || diffDeg < 3.0;

    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
      [sun, mercury],
      [sun.house],
      kundali,
      isCombust ? 60 : 78
    );

    yogas.push({
      id: 'classical_budhaditya',
      name: isCombust ? 'Budhaditya Yoga (Combust Mercury)' : 'Budhaditya Yoga',
      sanskritName: isCombust ? 'बुधादित्य योग (अस्त बुध)' : 'बुधादित्य योग',
      category: 'CLASSICAL',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `Sun and Mercury are conjunct in ${sun.sign} (${diffDeg.toFixed(1)}° separation).`,
      participatingPlanets: ['Sun', 'Mercury'],
      participatingHouses: [sun.house],
      classicalSource: 'Saravali, Ch. 35, Shloka 15',
      shlokaReference: 'सूर्येण सहितो सौम्यो बुधादित्येति विश्रुतः। बुद्धिमान् कीर्तिमान् दक्षो राजमान्यो विपश्चित्॥',
      description: {
        en: 'Formed by the solar-mercurial union in a single sign, synthesizing analytical intellect with radiant authority. Conferred brilliance in mathematics, commerce, and statecraft.',
        hi: 'सूर्य एवं बुध की युति से बुधादित्य योग बनता है। यह तीक्ष्ण विश्लेषणात्मक बुद्धि, शासकीय प्रतिष्ठा तथा व्यापारिक कुशलता प्रदान करता है।'
      },
      result: {
        en: 'Intellectual distinction, executive communication, and high analytical competence.',
        hi: 'बौद्धिक प्रखरता, प्रशासनिक दक्षता तथा त्वरित निर्णय क्षमता।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // C. Lakshmi Yoga (9th Lord in Kendra/Trikona and Lagna Lord Strong)
  const lagnaLordName = getBhaveshLord(1, ascSign);
  const ninthLordName = getBhaveshLord(9, ascSign);
  const lagnaLord = planets.find(p => p.name === lagnaLordName);
  const ninthLord = planets.find(p => p.name === ninthLordName);

  if (lagnaLord && ninthLord) {
    const isNinthInAuspicious = [1, 4, 7, 10, 5, 9].includes(ninthLord.house);
    const isLagnaStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lagnaLord.dignity as string) || (ninthLord.dignity as string) === 'Exalted';

    if (isNinthInAuspicious && isLagnaStrong) {
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [ninthLord, lagnaLord],
        [ninthLord.house, 9, 1],
        kundali,
        80
      );

      yogas.push({
        id: 'classical_lakshmi',
        name: 'Lakshmi Yoga',
        sanskritName: 'लक्ष्मी योग',
        category: 'CLASSICAL',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `9th Lord (${ninthLordName}) resides in auspicious house ${ninthLord.house} while Lagna Lord (${lagnaLordName}) is well dignified.`,
        participatingPlanets: [ninthLordName, lagnaLordName],
        participatingHouses: [ninthLord.house, 1, 9],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 41 / Phaladeepika, Ch. 6',
        shlokaReference: 'भाग्येशे केन्द्रगे वा त्रिकोणगे लग्नेशे बलयुते लक्ष्मीयोगः।',
        description: {
          en: 'A supreme wealth combination formed when the ruler of Fortune (9th) occupies an angular or trinal station while the Ascendant lord is potent, bestowing sustained prosperity, moral integrity, and grace.',
          hi: 'भाग्येश के केन्द्र या त्रिकोण में होने तथा लग्नेश के बलवान होने पर लक्ष्मी योग बनता है। यह जातक पर धन-धान्य तथा भगवती लक्ष्मी की अनवरत कृपा बरसाता है।'
        },
        result: {
          en: 'Abundant material fortune, noble character, high philanthropic disposition, and prosperity.',
          hi: 'अक्षय धन संपदा, उत्तम कुल प्रतिष्ठा तथा सुख-समृद्धि।'
        },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  // D. Amala Yoga (Unafflicted Benefic in 10th from Lagna or Moon)
  const tenthFromLagnaPlanets = planets.filter(p => p.house === 10 && NATURAL_BENEFICS.includes(p.name as PlanetName));
  const tenthFromMoonPlanets = moon ? planets.filter(p => getHouseDifference(p.house, moon.house) === 10 && NATURAL_BENEFICS.includes(p.name as PlanetName)) : [];

  if (tenthFromLagnaPlanets.length > 0 || tenthFromMoonPlanets.length > 0) {
    const amalaPlanets = Array.from(new Set([...tenthFromLagnaPlanets, ...tenthFromMoonPlanets]));
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
      amalaPlanets,
      [10],
      kundali,
      76
    );

    yogas.push({
      id: 'classical_amala',
      name: 'Amala Yoga',
      sanskritName: 'अमला योग',
      category: 'CLASSICAL',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `Benefic planet (${amalaPlanets.map(p => p.name).join(', ')}) occupies the 10th house of career from Lagna or Moon.`,
      participatingPlanets: amalaPlanets.map(p => p.name as PlanetName),
      participatingHouses: [10],
      classicalSource: 'Phaladeepika, Ch. 6, Shloka 21',
      shlokaReference: 'लग्नाद् वा शशिनो वापि दशमे शुभसंयुते। अमलाख्या भवेद्योगः कीर्तिमान् राजपूजितः॥',
      description: {
        en: 'Amala (Spotless) yoga manifests when a natural benefic graces the zenith 10th house, ensuring professional integrity, unblemished public fame, and ethical governance.',
        hi: 'लग्न अथवा चन्द्रमा से दशम भाव में शुभ ग्रह होने से अमला योग बनता है। यह निष्कलंक कीर्ति, व्यावसायिक सत्यनिष्ठा तथा राज्य सम्मान देता है।'
      },
      result: {
        en: 'Spotless reputation, honorable career advancements, and ethical professional authority.',
        hi: 'निर्मल यश, उच्च कार्यक्षेत्रीय सम्मान तथा समाज में प्रतिष्ठा।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 5. DHANA YOGAS EVALUATOR (Wealth Circuits: Houses 1, 2, 5, 9, 11)
// ----------------------------------------------------------------------------

export function evaluateDhanaYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const ascSign = kundali.planets.find(p => p.name === 'Ascendant')?.signIndex ?? 0;
  const planets = kundali.planets;

  const lord1 = getBhaveshLord(1, ascSign);
  const lord2 = getBhaveshLord(2, ascSign);
  const lord5 = getBhaveshLord(5, ascSign);
  const lord9 = getBhaveshLord(9, ascSign);
  const lord11 = getBhaveshLord(11, ascSign);

  const p1 = planets.find(p => p.name === lord1);
  const p2 = planets.find(p => p.name === lord2);
  const p5 = planets.find(p => p.name === lord5);
  const p9 = planets.find(p => p.name === lord9);
  const p11 = planets.find(p => p.name === lord11);

  // Circuit 1: 2nd Lord and 11th Lord connection (Exchange, Conjunction, Mutual Aspect)
  if (p2 && p11 && lord2 !== lord11) {
    const isYuti = p2.house === p11.house;
    const isParivartana = p2.house === 11 && p11.house === 2;
    const isAspect = areMutuallyAspecting(p2, p11);

    if (isYuti || isParivartana || isAspect) {
      const typeStr = isParivartana ? 'Sign Exchange (Parivartana)' : isYuti ? 'Conjunction (Yuti)' : 'Mutual Aspect (Drishti)';
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [p2, p11],
        [2, 11],
        kundali,
        82
      );

      yogas.push({
        id: 'dhana_circuit_2_11',
        name: `Maha Dhana-Labha Yoga (${typeStr})`,
        sanskritName: `महा धन-लाभ योग (${typeStr})`,
        category: 'DHANA',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `2nd Lord (${lord2}) and 11th Lord (${lord11}) form ${typeStr} between wealth and income houses.`,
        participatingPlanets: [lord2, lord11],
        participatingHouses: [2, 11, p2.house, p11.house],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 42 (Dhana Yoga Viveka)',
        shlokaReference: 'धनेशे लाभभावस्थे लाभेशे धनसंस्थिते। परस्परं दृष्टियुते महाधनपतिर्भवेत्॥',
        description: {
          en: 'A premier classical wealth circuit linking accumulated capital (2nd house) directly with perpetual income streams (11th house). Conferred compounding liquidity, business returns, and lineage prosperity.',
          hi: 'धन भाव (द्वितीय) एवं लाभ भाव (एकादश) के स्वामियों के मध्य संबंध होने से महा धन योग बनता है। यह जातक को प्रचुर धन संचय तथा निरंतर आय प्रदान करता है।'
        },
        result: {
          en: 'Continuous liquidity generation, robust savings accumulation, and high commercial viability.',
          hi: 'निरंतर धन लाभ, संचित कोष में वृद्धि तथा व्यापारिक संपन्नता।'
        },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  // Circuit 2: 5th Lord and 9th Lord with 1st or 2nd Lord (Lakshmi Sthana / Purva Punya Wealth)
  if (p5 && p9) {
    const isYuti59 = p5.house === p9.house;
    const isParivartana59 = p5.house === 9 && p9.house === 5;
    const isAspect59 = areMutuallyAspecting(p5, p9);

    if ((isYuti59 || isParivartana59 || isAspect59) && [1, 2, 5, 9, 11].includes(p5.house)) {
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [p5, p9],
        [5, 9],
        kundali,
        80
      );

      yogas.push({
        id: 'dhana_circuit_5_9',
        name: 'Trine Purva Punya Dhana Yoga',
        sanskritName: 'त्रिकोण पूर्वपुण्य धन योग',
        category: 'DHANA',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `5th Lord of intellect (${lord5}) and 9th Lord of fortune (${lord9}) form auspicious union in house ${p5.house}.`,
        participatingPlanets: [lord5, lord9],
        participatingHouses: [5, 9, p5.house],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 42',
        shlokaReference: 'धर्मपुत्रेशयोर्योगे धनवान् गुणवान् नरः।',
        description: {
          en: 'The auspicious union of the two supreme trine lords (5th & 9th) unifies past-life merits (Purva Punya) with divine grace (Bhagya), manifesting wealth through higher wisdom, intellect, and ethics.',
          hi: 'पंचमेश एवं नवमेश के संबंध से पूर्वपुण्य धन योग का निर्माण होता है। यह जातक को विद्या, बुद्धि तथा ईश्वरीय कृपा से प्रचुर धन दिलाता है।'
        },
        result: {
          en: 'Wealth acquired through expertise, intellectual ventures, advisory roles, and fortune.',
          hi: 'ज्ञान, विवेक तथा भाग्य के सहयोग से स्थायी समृद्धि।'
        },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: qualifying,
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  // Circuit 3: Natural Dhana Karaka Jupiter in 2nd or 11th house
  const jupiter = planets.find(p => p.name === 'Jupiter');
  if (jupiter && [2, 11].includes(jupiter.house)) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
      [jupiter],
      [jupiter.house],
      kundali,
      72
    );

    yogas.push({
      id: 'dhana_jupiter_wealth_house',
      name: 'Guru Dhana Sthana Yoga',
      sanskritName: 'गुरु धन स्थान योग',
      category: 'DHANA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `Natural wealth significator (Dhana Karaka) Jupiter occupies House ${jupiter.house} of wealth/gains.`,
      participatingPlanets: ['Jupiter'],
      participatingHouses: [jupiter.house],
      classicalSource: 'Saravali, Ch. 27',
      shlokaReference: 'वित्ते गुरौ प्रचुरवित्तसमन्वितो वा।',
      description: {
        en: 'Jupiter positioned in the 2nd (wealth) or 11th (gains) house expands ethical asset accumulation, financial resilience, and abundance.',
        hi: 'देवगुरु बृहस्पति का धन अथवा लाभ भाव में स्थित होना आर्थिक समृद्धि, सात्विक धन संचय तथा प्रचुर लाभ का द्योतक है।'
      },
      result: {
        en: 'Long-term financial security, family wealth preservation, and honorable gains.',
        hi: 'पारिवारिक सुख-समृद्धि, सात्विक आजीविका तथा सुरक्षित धन संचय।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 6. RAJA YOGAS EVALUATOR (Kendra-Trikona, Viparita, Neecha Bhanga)
// ----------------------------------------------------------------------------

export function evaluateRajaYogas(kundali: CompleteKundali): DetectedYoga[] {
  const yogas: DetectedYoga[] = [];
  const ascSign = kundali.planets.find(p => p.name === 'Ascendant')?.signIndex ?? 0;
  const planets = kundali.planets;

  // Kendra Lords: 1, 4, 7, 10
  // Trikona Lords: 1, 5, 9
  const kendraHouses = [1, 4, 7, 10];
  const trikonaHouses = [1, 5, 9];

  // A. Classical Kendra-Trikona Lord Combinations
  const kendraLords = Array.from(new Set(kendraHouses.map(h => ({ house: h, lord: getBhaveshLord(h, ascSign) }))));
  const trikonaLords = Array.from(new Set(trikonaHouses.map(h => ({ house: h, lord: getBhaveshLord(h, ascSign) }))));

  for (const k of kendraLords) {
    for (const t of trikonaLords) {
      if (k.lord === t.lord) continue; // Same planet ruling both (like Mars for Cancer/Leo) handled as Yogakaraka below

      const pk = planets.find(p => p.name === k.lord);
      const pt = planets.find(p => p.name === t.lord);
      if (!pk || !pt) continue;

      const isYuti = pk.house === pt.house;
      const isParivartana = pk.house === t.house && pt.house === k.house;
      const isAspect = areMutuallyAspecting(pk, pt);

      if ((isYuti || isParivartana || isAspect) && ![6, 8, 12].includes(pk.house)) {
        const isDharmaKarma = (k.house === 10 && t.house === 9) || (k.house === 9 && t.house === 10);
        const yogaName = isDharmaKarma ? 'Dharma-Karmadhipati Raja Yoga' : `Kendra-Trikona Raja Yoga (H${k.house} & H${t.house})`;
        const yogaId = isDharmaKarma ? 'raja_dharma_karmadhipati' : `raja_kendra_trikona_${k.house}_${t.house}`;

        // Deduplicate
        if (!yogas.some(y => y.id === yogaId)) {
          const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
            [pk, pt],
            [pk.house, pt.house],
            kundali,
            isDharmaKarma ? 88 : 78
          );

          yogas.push({
            id: yogaId,
            name: yogaName,
            sanskritName: isDharmaKarma ? 'धर्म-कर्माधिपति राजयोग' : `केन्द्र-त्रिकोण राजयोग (भाव ${k.house} व ${t.house})`,
            category: 'RAJA',
            methodologyCategory: 'CLASSICAL_CONSENSUS',
            formationRule: `Kendra Lord of House ${k.house} (${k.lord}) and Trikona Lord of House ${t.house} (${t.lord}) form auspicious mutual alignment.`,
            participatingPlanets: [k.lord, t.lord],
            participatingHouses: [k.house, t.house, pk.house],
            classicalSource: 'Brihat Parashara Hora Shastra, Ch. 34 (Raja Yoga Adhyaya) / Phaladeepika Ch. 6',
            shlokaReference: 'केन्द्राधिपाश्च त्रिकोणाधिपाश्च संबन्धेन परस्परम्। राजयोगकरा ज्ञेया जातकस्य यशस्विनः॥',
            description: {
              en: isDharmaKarma
                ? 'The supreme pinnacle Raja Yoga formed by the mutual relationship of the 9th lord of Fortune and 10th lord of Career. Bestows sovereign authority, ethical leadership, and extensive social honors.'
                : `A powerful Parashari sovereign combination created by the alliance of Kendra lord of action with Trikona lord of grace in house ${pk.house}.`,
              hi: isDharmaKarma
                ? 'नवमेश (भाग्य) एवं दशमेश (कर्म) के मध्य संबंध से सर्वोच्च धर्म-कर्माधिपति राजयोग बनता है। यह जातक को उच्च पद, राज्य सम्मान तथा यशस्वी बनाता है।'
                : `केन्द्रेश एवं त्रिकोणेश के शुभ संबंध से राजयोग निर्मित होता है, जो पद, प्रतिष्ठा एवं अधिकार प्रदान करता है।`
            },
            result: {
              en: 'Elevated professional stature, institutional authority, and public respect.',
              hi: 'कार्यक्षेत्र में उच्च पद, प्रशासनिक सम्मान तथा दीर्घकालिक प्रभाव।'
            },
            isAuspicious: true,
            strengthScore,
            fruitionStatus,
            qualifyingFactors: qualifying,
            cancellationOrAfflictionFactors: afflictions
          });
        }
      }
    }
  }

  // B. Yogakaraka Planet Single-Handed Raja Yoga (e.g. Mars for Cancer/Leo, Saturn for Taurus/Libra, Venus for Capricorn/Aquarius)
  for (const p of SEVEN_CLASSICAL_PLANETS) {
    const ruledHouses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter(h => getBhaveshLord(h, ascSign) === p);
    const hasKendra = ruledHouses.some(h => [4, 7, 10].includes(h));
    const hasTrikona = ruledHouses.some(h => [5, 9].includes(h));

    if (hasKendra && hasTrikona) {
      const pl = planets.find(item => item.name === p);
      if (pl && ![6, 8, 12].includes(pl.house)) {
        const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
          [pl],
          [pl.house, ...ruledHouses],
          kundali,
          80
        );

        yogas.push({
          id: `raja_yogakaraka_${p.toLowerCase()}`,
          name: `Yogakaraka Sovereign Potential (${p})`,
          sanskritName: `योगकारक राजयोग प्रभाव (${p})`,
          category: 'RAJA',
          methodologyCategory: 'CLASSICAL_CONSENSUS',
          formationRule: `${p} singularly rules both a Kendra (House ${ruledHouses.filter(h => [4,7,10].includes(h)).join('/')}) and a Trikona (House ${ruledHouses.filter(h => [5,9].includes(h)).join('/')}) for this ascendant.`,
          participatingPlanets: [p],
          participatingHouses: [pl.house, ...ruledHouses],
          classicalSource: 'Brihat Parashara Hora Shastra, Ch. 34, Shloka 13',
          shlokaReference: 'केन्द्रत्रिकोणनेतारौ दोषयुक्तौ न चेद् भवेताम्। योगकारको भवेदेकः॥',
          description: {
            en: `${p} is the single Yogakaraka planet for ${ZODIAC_SIGNS[ascSign]} ascendant, capable of bestowing extraordinary success, leadership, and prosperity during its Dasha.`,
            hi: `${ZODIAC_SIGNS[ascSign]} लग्न के लिए ${p} अद्वितीय योगकारक ग्रह है, जो अपनी दशा में अभूतपूर्व उन्नति एवं नेतृत्व प्रदान करता है।`
          },
          result: {
            en: 'Catalytic life acceleration, sovereign authority, and prosperity during its Dasha periods.',
            hi: 'दशा काल में सर्वांगीण उन्नति, अधिकार वृद्धि तथा मान-सम्मान।'
          },
          isAuspicious: true,
          strengthScore,
          fruitionStatus,
          qualifyingFactors: qualifying,
          cancellationOrAfflictionFactors: afflictions
        });
      }
    }
  }

  // C. Viparita Raja Yogas (Harsha, Sarala, Vimala)
  const lord1 = getBhaveshLord(1, ascSign);
  const lord6 = getBhaveshLord(6, ascSign);
  const lord8 = getBhaveshLord(8, ascSign);
  const lord12 = getBhaveshLord(12, ascSign);

  const p6 = planets.find(p => p.name === lord6);
  const p8 = planets.find(p => p.name === lord8);
  const p12 = planets.find(p => p.name === lord12);

  // Harsha Yoga: 6th lord in 6th, 8th, or 12th
  if (p6 && [6, 8, 12].includes(p6.house)) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics([p6], [p6.house], kundali, 72);
    yogas.push({
      id: 'viparita_harsha',
      name: 'Harsha Yoga (Viparita Raja Yoga)',
      sanskritName: 'हर्ष योग (विपरीत राजयोग)',
      category: 'RAJA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `6th Lord (${lord6}) is confined to dusthana house ${p6.house}, neutralizing adversaries and health challenges.`,
      participatingPlanets: [lord6],
      participatingHouses: [6, p6.house],
      classicalSource: 'Phaladeepika, Ch. 6, Shloka 63',
      shlokaReference: 'षष्ठेशे षष्ठाष्टमव्ययगे हर्षयोगः। सुखभोगसमन्वितो दृढगात्रो रिपुहन्ता॥',
      description: {
        en: 'Harsha yoga forms when the 6th lord is placed in a trik dusthana, destroying foes, conferring robust immunity, resilience against litigation, and joy through overcoming obstacles.',
        hi: 'षष्ठेश के ६, ८ या १२वें भाव में होने से हर्ष विपरीत राजयोग बनता है। यह शत्रुओं पर विजय, उत्तम आरोग्य तथा संकटों से मुक्ति देता है।'
      },
      result: {
        en: 'Decisive advantage in legal disputes, disease immunity, and triumph over competition.',
        hi: 'शत्रुओं पर विजय, मुकदमों में सफलता तथा कठिनाइयों से उबरने की क्षमता।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // Sarala Yoga: 8th lord in 6th, 8th, or 12th
  if (p8 && [6, 8, 12].includes(p8.house) && lord8 !== lord1) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics([p8], [p8.house], kundali, 74);
    yogas.push({
      id: 'viparita_sarala',
      name: 'Sarala Yoga (Viparita Raja Yoga)',
      sanskritName: 'सरल योग (विपरीत राजयोग)',
      category: 'RAJA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `8th Lord (${lord8}) is placed in dusthana house ${p8.house}, turning transformative crises into breakthrough triumphs.`,
      participatingPlanets: [lord8],
      participatingHouses: [8, p8.house],
      classicalSource: 'Phaladeepika, Ch. 6, Shloka 65',
      shlokaReference: 'रन्ध्रेशे षष्ठाष्टमव्ययगे सरलयुक्तः। दीर्घायुः धीरो विद्यावान् कीर्तिमान्॥',
      description: {
        en: 'Sarala yoga forms when the 8th lord occupies a dusthana, endowing fearlessness, longevity, unexpected breakthrough gains from turbulence, and occult acumen.',
        hi: 'अष्टमेश के ६, ८ या १२वें भाव में होने से सरल विपरीत राजयोग बनता है। यह दीर्घायु, निर्भीकता तथा संकटों से अप्रत्याशित लाभ दिलाता है।'
      },
      result: {
        en: 'Longevity protection, breakthrough resilience during crises, and unearned asset gains.',
        hi: 'दीर्घायु, संकटों में अचल धैर्य तथा गुप्त स्रोतों से लाभ।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // Vimala Yoga: 12th lord in 6th, 8th, or 12th
  if (p12 && [6, 8, 12].includes(p12.house)) {
    const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics([p12], [p12.house], kundali, 70);
    yogas.push({
      id: 'viparita_vimala',
      name: 'Vimala Yoga (Viparita Raja Yoga)',
      sanskritName: 'विमल योग (विपरीत राजयोग)',
      category: 'RAJA',
      methodologyCategory: 'CLASSICAL_CONSENSUS',
      formationRule: `12th Lord (${lord12}) resides in dusthana house ${p12.house}, curtailing unnecessary expenditures and elevating noble independence.`,
      participatingPlanets: [lord12],
      participatingHouses: [12, p12.house],
      classicalSource: 'Phaladeepika, Ch. 6, Shloka 69',
      shlokaReference: 'व्ययेशे षष्ठाष्टमव्ययगे विमलयोगः। मितव्ययो धनवान् सुखी स्वतंत्रचेता॥',
      description: {
        en: 'Vimala yoga forms when the 12th lord is enclosed in a dusthana, curbing wasteful leakage, conferring independent spirit, spiritual contentment, and righteous living.',
        hi: 'द्वादशेश के ६, ८ या १२वें भाव में होने से विमल विपरीत राजयोग बनता है। यह व्यर्थ व्यय को रोकता है तथा स्वावलंबी व सुखी बनाता है।'
      },
      result: {
        en: 'Prudent financial containment, freedom from debt bondage, and inner peace.',
        hi: 'मितव्ययिता, ऋणमुक्ति तथा स्वतंत्र गौरवशाली जीवन।'
      },
      isAuspicious: true,
      strengthScore,
      fruitionStatus,
      qualifyingFactors: qualifying,
      cancellationOrAfflictionFactors: afflictions
    });
  }

  // D. Neecha Bhanga Raja Yoga (NBRY)
  // Check any debilitated planet in D1
  const debilitatedPlanets = planets.filter(p => p.dignity === 'Debilitated' && p.name !== 'Ascendant' && p.name !== 'Rahu' && p.name !== 'Ketu');
  const moonPos = planets.find(p => p.name === 'Moon');

  for (const debPl of debilitatedPlanets) {
    const debSign = debPl.signIndex;
    const dispositorLord = getLordOfSign(debSign);
    const dispositorPl = planets.find(p => p.name === dispositorLord);

    const exaltLordOfSign = getLordOfSign(EXALTATION_SIGNS[debPl.name as PlanetName]);
    const exaltLordPl = planets.find(p => p.name === exaltLordOfSign);

    let nbryTriggered = false;
    const nbryReasons: string[] = [];

    // Condition 1: Dispositor of debilitated planet is in Kendra from Lagna or Moon
    if (dispositorPl) {
      if ([1, 4, 7, 10].includes(dispositorPl.house)) {
        nbryTriggered = true;
        nbryReasons.push(`Dispositor ${dispositorLord} is in Kendra (H${dispositorPl.house}) from Lagna.`);
      }
      if (moonPos && [1, 4, 7, 10].includes(getHouseDifference(dispositorPl.house, moonPos.house))) {
        nbryTriggered = true;
        nbryReasons.push(`Dispositor ${dispositorLord} is in Kendra from Moon.`);
      }
    }

    // Condition 2: Planet that gets exalted in this debilitation sign is in Kendra from Lagna or Moon
    const planetExaltedInDebSignName = (Object.keys(EXALTATION_SIGNS) as PlanetName[]).find(
      k => EXALTATION_SIGNS[k] === debSign
    );
    if (planetExaltedInDebSignName) {
      const plExaltedInSign = planets.find(p => p.name === planetExaltedInDebSignName);
      if (plExaltedInSign && [1, 4, 7, 10].includes(plExaltedInSign.house)) {
        nbryTriggered = true;
        nbryReasons.push(`${planetExaltedInDebSignName} (which exalts in ${debPl.sign}) is in Kendra (H${plExaltedInSign.house}).`);
      }
    }

    // Condition 3: Debilitated planet is aspected by its dispositor
    if (dispositorPl && areMutuallyAspecting(debPl, dispositorPl)) {
      nbryTriggered = true;
      nbryReasons.push(`Debilitated ${debPl.name} is aspected by its sign dispositor ${dispositorLord}.`);
    }

    if (nbryTriggered) {
      const { strengthScore, fruitionStatus, qualifying, afflictions } = computeFruitionMetrics(
        [debPl, ...(dispositorPl ? [dispositorPl] : [])],
        [debPl.house],
        kundali,
        74
      );

      yogas.push({
        id: `raja_nbry_${debPl.name.toLowerCase()}`,
        name: `Neecha Bhanga Raja Yoga (${debPl.name})`,
        sanskritName: `नीचभंग राजयोग (${debPl.name})`,
        category: 'RAJA',
        methodologyCategory: 'CLASSICAL_CONSENSUS',
        formationRule: `Debilitation of ${debPl.name} in ${debPl.sign} is cancelled by classical Parashari rules: ${nbryReasons.join(' ')}`,
        participatingPlanets: [debPl.name as PlanetName, ...(dispositorPl ? [dispositorLord] : [])],
        participatingHouses: [debPl.house],
        classicalSource: 'Brihat Parashara Hora Shastra, Ch. 38 / Phaladeepika Ch. 6, Shloka 26–30',
        shlokaReference: 'नीचस्थितो जन्मनि यो ग्रहः स्यात् तद्राशिनाथोऽपि तदुच्चनाथः। चन्द्राद् यदि वा विलग्नात् केन्द्रे स्थितश्चेत् नृपतिं करोति॥',
        description: {
          en: `Neecha Bhanga Raja Yoga cancels the debilitation of ${debPl.name}, converting an initial karmic liability into an extraordinary engine of mature triumph, humility, and eventual sovereign distinction.`,
          hi: `${debPl.name} की नीचता का शास्त्रीय परिहार होकर नीचभंग राजयोग का सृजन हो रहा है। यह प्रारंभिक संघर्षों को दीर्घकालिक सफलता व प्रतिष्ठा में रूपांतरित करता है।`
        },
        result: {
          en: 'Initial adversity transformed into hard-won authority and profound life mastery.',
          hi: 'आरंभिक कठिनाइयों के उपरांत उच्च पद, ख्याति तथा स्थायी सफलता।'
        },
        isAuspicious: true,
        strengthScore,
        fruitionStatus,
        qualifyingFactors: [...qualifying, ...nbryReasons],
        cancellationOrAfflictionFactors: afflictions
      });
    }
  }

  return yogas;
}

// ----------------------------------------------------------------------------
// 7. CANONICAL MANGLIK DOSHA (KUJA DOSHA) EVALUATOR
// ----------------------------------------------------------------------------

export function evaluateManglikDosha(kundali: CompleteKundali): ManglikAnalysis {
  const mars = kundali.planets.find(p => p.name === 'Mars');
  const moon = kundali.planets.find(p => p.name === 'Moon');
  const venus = kundali.planets.find(p => p.name === 'Venus');
  const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
  const sun = kundali.planets.find(p => p.name === 'Sun');

  if (!mars) {
    return {
      isManglik: false,
      severityPercent: 0,
      placements: [],
      cancellations: [],
      summary: 'Mars position not verified.',
      remedies: []
    };
  }

  const MANGLIK_HOUSES = [1, 2, 4, 7, 8, 12];

  const marsHouseFromLagna = mars.house;
  const marsHouseFromMoon = moon ? getHouseDifference(mars.house, moon.house) : undefined;
  const marsHouseFromVenus = venus ? getHouseDifference(mars.house, venus.house) : undefined;

  const isLagnaManglik = MANGLIK_HOUSES.includes(marsHouseFromLagna);
  const isMoonManglik = marsHouseFromMoon !== undefined && MANGLIK_HOUSES.includes(marsHouseFromMoon);
  const isVenusManglik = marsHouseFromVenus !== undefined && MANGLIK_HOUSES.includes(marsHouseFromVenus);

  const placements: string[] = [];
  if (isLagnaManglik) placements.push(`Mars in House ${marsHouseFromLagna} from Lagna`);
  if (isMoonManglik) placements.push(`Mars in House ${marsHouseFromMoon} from Moon`);
  if (isVenusManglik) placements.push(`Mars in House ${marsHouseFromVenus} from Venus`);

  const isManglik = placements.length > 0;
  const cancellations: string[] = [];

  // Classical Parashari & Muhurta Chintamani Cancellations:
  // 1. Mars in Own Sign (Aries, Scorpio) or Exalted (Capricorn)
  if (mars.sign === 'Aries' || mars.sign === 'Scorpio') {
    cancellations.push('Mars is in its Own Sign (Aries/Scorpio), pacifying fiery affliction.');
  }
  if (mars.sign === 'Capricorn') {
    cancellations.push('Mars is Exalted in Capricorn, converting aggression into disciplined leadership.');
  }

  // 2. Specific House-Sign Exceptions:
  // - 2nd house in Gemini or Virgo (Mercury signs)
  if (marsHouseFromLagna === 2 && (mars.sign === 'Gemini' || mars.sign === 'Virgo')) {
    cancellations.push('Mars in 2nd house in Mercury sign (Gemini/Virgo) nullifies Kuja Dosha.');
  }
  // - 4th house in Aries or Scorpio
  if (marsHouseFromLagna === 4 && (mars.sign === 'Aries' || mars.sign === 'Scorpio')) {
    cancellations.push('Mars in 4th house in own sign eliminates domestic friction.');
  }
  // - 7th house in Capricorn or Pisces
  if (marsHouseFromLagna === 7 && (mars.sign === 'Capricorn' || mars.sign === 'Pisces')) {
    cancellations.push('Mars in 7th house in Capricorn (exalted) or Pisces (friendly) cancels marital dosha.');
  }
  // - 8th house in Sagittarius or Pisces (Jupiter signs) or Cancer
  if (marsHouseFromLagna === 8 && (mars.sign === 'Sagittarius' || mars.sign === 'Pisces')) {
    cancellations.push('Mars in 8th house in Jupiter sign (Sagittarius/Pisces) neutralizes dosha.');
  }
  // - 12th house in Taurus or Libra (Venus signs)
  if (marsHouseFromLagna === 12 && (mars.sign === 'Taurus' || mars.sign === 'Libra')) {
    cancellations.push('Mars in 12th house in Venus sign (Taurus/Libra) nullifies adverse expenditure.');
  }

  // 3. Benefic Jupiter or Moon Aspect / Conjunction
  if (jupiter) {
    const isJupConjunct = jupiter.house === mars.house;
    const jupAspectDist = getHouseDifference(mars.house, jupiter.house);
    const isJupAspecting = [5, 7, 9].includes(jupAspectDist);
    if (isJupConjunct || isJupAspecting) {
      cancellations.push('Benefic Jupiter conjuncts or aspects Mars (Guru Drishti provides supreme classical pacification).');
    }
  }

  // 4. Mars Combust by Sun
  if (mars.isCombust || (sun && mars.house === sun.house && Math.abs(mars.degreeInSign - sun.degreeInSign) < 5.0)) {
    cancellations.push('Mars is combust (Asta), significantly attenuating its independent malefic manifestation.');
  }

  // Calculate severity percentage grounded in classical rules
  let rawSeverity = 0;
  if (isManglik) {
    if (isLagnaManglik && isMoonManglik && isVenusManglik) rawSeverity = 90;
    else if (isLagnaManglik && isMoonManglik) rawSeverity = 75;
    else if (isLagnaManglik) rawSeverity = 60;
    else rawSeverity = 40;

    // Houses 7 and 8 carry greater traditional weight
    if (marsHouseFromLagna === 7 || marsHouseFromLagna === 8) rawSeverity += 10;
  }

  const isEffectivelyMitigated = cancellations.length > 0;
  const severityPercent = isEffectivelyMitigated ? Math.max(10, rawSeverity - 45) : rawSeverity;

  const remedies = isManglik ? [
    'Chant the Mangal Gayatri or Beej Mantra ("Om Kram Kreem Kroum Sah Bhaumaya Namah") with peaceful devotion.',
    'Cultivate mindful communication, active listening, and calm patience in interpersonal relationships.',
    'Channel energetic initiative through physical yoga, regular exercise, or charitable deeds on Tuesdays.',
    'Worship Lord Hanuman or recite Hanuman Chalisa to harmoniously elevate Martian energy.'
  ] : [];

  const summary = !isManglik
    ? 'No Manglik Dosha detected in the birth chart. Mars is placed outside the sensitive marital houses.'
    : isEffectivelyMitigated
      ? `Manglik presence detected (${placements.length} reference${placements.length > 1 ? 's' : ''}), but neutralized by ${cancellations.length} classical cancellation factor${cancellations.length > 1 ? 's' : ''}.`
      : `Active Manglik placement (${placements.length} reference${placements.length > 1 ? 's' : ''}). Conscious relational awareness and maturity recommended.`;

  return {
    isManglik,
    severityPercent,
    placements,
    cancellations,
    summary,
    remedies,
    isLagnaManglik,
    isMoonManglik,
    isVenusManglik,
    marsPlacementFromLagna: marsHouseFromLagna,
    marsPlacementFromMoon: marsHouseFromMoon,
    marsPlacementFromVenus: marsHouseFromVenus,
    isEffectivelyMitigated,
    verdictEn: !isManglik 
      ? 'Completely free from Manglik affliction.'
      : isEffectivelyMitigated
        ? 'Manglik presence is effectively mitigated by classical Parashari exemptions.'
        : 'Active Manglik placement; balanced matching and relational mindfulness recommended.',
    verdictHi: !isManglik
      ? 'कुण्डली पूर्णतः मांगलिक दोष से मुक्त है।'
      : isEffectivelyMitigated
        ? 'शास्त्रीय परिहार नियमों के कारण मांगलिक प्रभाव शांत एवं निष्प्रभावी है।'
        : 'सक्रिय मांगलिक स्थिति; आपसी संवाद तथा संतुलित वैवाहिक समन्वय हितकर है।'
  };
}

// ----------------------------------------------------------------------------
// 8. CANONICAL KAAL SARP & KALAMRITA YOGA EVALUATOR
// ----------------------------------------------------------------------------

export function evaluateKaalSarpYoga(kundali: CompleteKundali): KaalSarpAnalysis {
  const rahu = kundali.planets.find(p => p.name === 'Rahu');
  const ketu = kundali.planets.find(p => p.name === 'Ketu');

  if (!rahu || !ketu) {
    return {
      hasKaalSarp: false,
      type: 'None',
      description: 'Rahu or Ketu coordinates missing.',
      remedies: []
    };
  }

  const sevenPlanets = kundali.planets.filter(p => SEVEN_CLASSICAL_PLANETS.includes(p.name as PlanetName));
  const rahuLon = rahu.longitude;
  const ketuLon = ketu.longitude;

  // Semicircle from Rahu to Ketu (direct zodiac order)
  let inRahuToKetuSide = 0;
  let inKetuToRahuSide = 0;
  const escapingPlanets: PlanetName[] = [];

  for (const p of sevenPlanets) {
    // Normal distance moving forward from Rahu to planet
    const distFromRahu = (p.longitude - rahuLon + 360) % 360;
    
    // Boundary orb: If planet is within 0.5 degrees of Rahu or Ketu, evaluate boundary escape
    const distFromKetu = (p.longitude - ketuLon + 360) % 360;
    const isVeryCloseToRahu = Math.min(distFromRahu, 360 - distFromRahu) < 0.5;
    const isVeryCloseToKetu = Math.min(distFromKetu, 360 - distFromKetu) < 0.5;

    if (distFromRahu < 180.0) {
      inRahuToKetuSide++;
    } else {
      inKetuToRahuSide++;
    }
  }

  const KAAL_SARP_TYPES: Record<number, string> = {
    1: 'Anant Kaal Sarp (Rahu in 1st, Ketu in 7th)',
    2: 'Kulik Kaal Sarp (Rahu in 2nd, Ketu in 8th)',
    3: 'Vasuki Kaal Sarp (Rahu in 3rd, Ketu in 9th)',
    4: 'Shankhpal Kaal Sarp (Rahu in 4th, Ketu in 10th)',
    5: 'Padma Kaal Sarp (Rahu in 5th, Ketu in 11th)',
    6: 'Mahapadma Kaal Sarp (Rahu in 6th, Ketu in 12th)',
    7: 'Takshak Kaal Sarp (Rahu in 7th, Ketu in 1st)',
    8: 'Karkotak Kaal Sarp (Rahu in 8th, Ketu in 2nd)',
    9: 'Shankhachud Kaal Sarp (Rahu in 9th, Ketu in 3rd)',
    10: 'Ghatak Kaal Sarp (Rahu in 10th, Ketu in 4th)',
    11: 'Vishdhar Kaal Sarp (Rahu in 11th, Ketu in 5th)',
    12: 'Sheshnag Kaal Sarp (Rahu in 12th, Ketu in 6th)'
  };

  const isCompleteHemmed = inRahuToKetuSide === 7 || inKetuToRahuSide === 7;
  const isPartial = inRahuToKetuSide === 6 || inKetuToRahuSide === 6;

  // Identify escaping planet if partial
  if (isPartial) {
    const isMinoritySideRahuToKetu = inRahuToKetuSide === 1;
    for (const p of sevenPlanets) {
      const distFromRahu = (p.longitude - rahuLon + 360) % 360;
      const onRahuToKetuSide = distFromRahu < 180.0;
      if (onRahuToKetuSide === isMinoritySideRahuToKetu) {
        escapingPlanets.push(p.name as PlanetName);
      }
    }
  }

  // Direction: If planets are in Rahu->Ketu, Rahu is the head/entry point (Classical Kaal Sarp).
  // If planets are in Ketu->Rahu, Ketu is the head/entry point (Kalamrita Yoga, spiritually liberating).
  const isKalamrita = inKetuToRahuSide === 7;
  const hasKaalSarp = isCompleteHemmed;

  // Classical Cancellations
  const cancellations: string[] = [];
  const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
  if (jupiter && [1, 4, 7, 10].includes(jupiter.house)) {
    cancellations.push('Jupiter placed in an angular Kendra pacifies nodal containment.');
  }
  const exaltedPlanets = sevenPlanets.filter(p => p.dignity === 'Exalted');
  if (exaltedPlanets.length >= 2) {
    cancellations.push(`Multiple exalted planets (${exaltedPlanets.map(p => p.name).join(', ')}) neutralize negative nodal restrictions.`);
  }

  const remedies = (hasKaalSarp || isPartial) ? [
    'Chant the Maha Mrityunjaya Mantra ("Om Tryambakam Yajamahe...") daily with calm devotion.',
    'Cultivate spiritual discernment and regular meditation to transcend cyclic restlessness.',
    'Perform selfless community service, feed birds and stray animals on Saturdays.',
    'Offer milk, water, and bilva leaves to Lord Shiva Lingam on Mondays.'
  ] : [];

  let typeName = 'None';
  let desc = 'Planets are naturally distributed across both sides of the Rahu-Ketu nodal axis.';

  if (hasKaalSarp) {
    const baseType = KAAL_SARP_TYPES[rahu.house] || 'Classical Kaal Sarp';
    typeName = isKalamrita ? `Kalamrita Yoga (${baseType})` : baseType;
    desc = isKalamrita
      ? `Kalamrita Yoga is formed as all classical planets are hemmed moving towards Ketu. Traditionally regarded as an auspicious configuration for spiritual illumination, ascetic focus, and breakthrough wisdom.`
      : `Kaal Sarp Yoga is formed as all seven classical planets are hemmed on one side of the Rahu-Ketu axis. Often marks early life intensity followed by profound mid-life perseverance and self-made authority.`;
  } else if (isPartial) {
    typeName = `Partial Kaal Sarp / Bhanga (${escapingPlanets.join(', ')} escaping)`;
    desc = `Partial nodal containment: 6 planets are hemmed while ${escapingPlanets.join(', ')} breaks the axis, significantly diffusing the intensity and forming Kaal Sarp Bhanga.`;
  }

  return {
    hasKaalSarp: hasKaalSarp || isPartial,
    type: typeName,
    description: desc,
    remedies,
    isKalamrita,
    isPartial,
    hemmedCount: Math.max(inRahuToKetuSide, inKetuToRahuSide),
    escapingPlanets,
    rahuHouse: rahu.house,
    ketuHouse: ketu.house,
    cancellations,
    verdictEn: !hasKaalSarp && !isPartial
      ? 'Completely free from Kaal Sarp containment.'
      : isKalamrita
        ? 'Kalamrita Yoga active: spiritually elevating nodal configuration.'
        : isPartial
          ? `Partial Kaal Sarp / Bhanga: mitigated by escaping planet ${escapingPlanets.join(', ')}.`
          : 'Complete Kaal Sarp configuration: calls for disciplined perseverance and spiritual focus.',
    verdictHi: !hasKaalSarp && !isPartial
      ? 'कुण्डली कालसर्प योग से पूर्णतः मुक्त है।'
      : isKalamrita
        ? 'कालअमृत योग सक्रिय: आध्यात्मिक उन्नति व आत्मज्ञान हेतु शुभ।'
        : isPartial
          ? `आंशिक कालसर्प / भंग योग: ग्रह ${escapingPlanets.join(', ')} द्वारा दोष शांत।`
          : 'कालसर्प योग उपस्थित: धैर्यपूर्ण संकल्प तथा नियमित साधना से भाग्योदय।'
  };
}

// ----------------------------------------------------------------------------
// 9. MASTER CANONICAL EVALUATOR (All Yogas & Doshas)
// ----------------------------------------------------------------------------

export function evaluateAllYogasAndDoshas(kundali: CompleteKundali): YogaEvaluationResult {
  const nabhasaYogas = evaluateNabhasaYogas(kundali);
  const mahapurushaYogas = evaluatePanchaMahapurushaYogas(kundali);
  const solarLunarYogas = evaluateSolarLunarYogas(kundali);
  const classicalYogas = evaluateClassicalGrahaYogas(kundali);
  const dhanaYogas = evaluateDhanaYogas(kundali);
  const rajaYogas = evaluateRajaYogas(kundali);

  const allYogas: DetectedYoga[] = [
    ...nabhasaYogas,
    ...mahapurushaYogas,
    ...rajaYogas,
    ...dhanaYogas,
    ...classicalYogas,
    ...solarLunarYogas
  ].map(y => ({
    ...y,
    formationStatus: y.formationStatus || 'FORMED',
    fruitionMethodologyNotes: y.fruitionMethodologyNotes || 'ENGINEERING / PRODUCT METHODOLOGY: Geometric formation in D1 establishes classical baseline; Layer 6 SAV (>= 28) and Layer 7 Shadbala (>= 1.0) modulate manifest fruition.',
    epistemicTaxonomy: y.epistemicTaxonomy || {
      calculatedFact: `Formed geometrically by ${y.participatingPlanets.join(', ')} in houses ${y.participatingHouses.join(', ')}.`,
      classicalRule: `${y.classicalSource}: ${y.formationRule}`,
      fruitionAssessment: `Fruition classification: ${y.fruitionStatus} (Score: ${y.strengthScore}/100).`
    }
  }));

  const manglik = evaluateManglikDosha(kundali);
  const kaalSarp = evaluateKaalSarpYoga(kundali);

  const highPotencyCount = allYogas.filter(y => y.fruitionStatus === 'HIGH_POTENCY').length;
  const moderatePotencyCount = allYogas.filter(y => y.fruitionStatus === 'MODERATE_POTENCY').length;
  const dormantOrAfflictedCount = allYogas.filter(y => y.fruitionStatus === 'AFFLICTED_OR_DORMANT').length;

  return {
    yogas: allYogas,
    manglik,
    kaalSarp,
    summary: {
      totalDetected: allYogas.length,
      highPotencyCount,
      moderatePotencyCount,
      dormantOrAfflictedCount,
      hasMajorRajaYoga: rajaYogas.some(y => y.fruitionStatus !== 'AFFLICTED_OR_DORMANT'),
      hasMajorDhanaYoga: dhanaYogas.some(y => y.fruitionStatus !== 'AFFLICTED_OR_DORMANT'),
      hasMahapurushaYoga: mahapurushaYogas.some(y => y.fruitionStatus !== 'AFFLICTED_OR_DORMANT')
    }
  };
}
